import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from "react";
import {
  Download,
  Filter,
  Calendar,
  Users,
  Clock,
  DollarSign,
  X,
  FileText,
  ClipboardList,
  UserCheck,
  ArrowRightLeft,
  DollarSign as DollarIcon,
  UserX,
  RefreshCw,
} from "lucide-react";
import {
  DsButton,
  DsIcon,
  DsPageHeader,
  DsSpinner,
} from "../../design-system";
import SafeTrendingUp from "../Common/SafeTrendingUp";
import Payslips from "./Payslips";
import Form16 from "./Form16";
import Pagination from "../Common/Pagination";
import Filters from "./Filters";
import ReportFiltersPopup from "./ReportFiltersPopup";
import DatePicker from "../Common/DatePicker";
import {
  clampYm,
  resolveCompanyMinMonthValue,
} from "../../utils/selectableDateBounds";
import {
  employeeService,
  branchService,
  departmentService,
  designationService,
  gradesService,
  categoryService,
  subDepartmentService,
  salaryService,
  attendanceService,
} from "../../services/api";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

// Helper function to format numbers in Indian format (e.g., 3,00,000)
const formatIndianNumber = (num) => {
  if (num === null || num === undefined || num === "") return "";
  
  const numValue = parseFloat(num);
  if (isNaN(numValue)) return "";
  
  // Convert to integer (remove decimals)
  const integerPart = Math.floor(numValue);
  
  // Convert to string and reverse for easier processing
  const numStr = integerPart.toString();
  const reversed = numStr.split('').reverse();
  
  let result = [];
  for (let i = 0; i < reversed.length; i++) {
    // Add comma after first 3 digits
    if (i === 3) {
      result.push(',');
    }
    // Add comma every 2 digits after the first 3
    if (i > 3 && (i - 3) % 2 === 0) {
      result.push(',');
    }
    result.push(reversed[i]);
  }
  
  // Reverse back and join
  return result.reverse().join('');
};

// Helper to get company code for filenames from localStorage companyData.code
const getCompanyCodeForReports = () => {
  let companyCode = "COMPANY";
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("companyData") : null;
    if (raw) {
      const company = JSON.parse(raw);
      if (company && company.code) {
        companyCode = String(company.code).trim().toUpperCase() || companyCode;
      }
    }
  } catch {
    // ignore, fall back to default
  }
  return companyCode;
};

// Helper to build report filename with underscores between parts:
// <COMPANYCODE>_<Report_Name_With_Underscores>_<Suffix>.xlsx
const buildReportFilename = (companyCode, reportLabel, suffix) => {
  const safeCompany = (companyCode || "COMPANY").toString().trim().toUpperCase();
  const safeLabel = (reportLabel || "Report").toString().trim().replace(/\s+/g, "_");
  const safeSuffix = (suffix || "").toString().trim().replace(/\s+/g, "_");
  return `${safeCompany}_${safeLabel}${safeSuffix ? `_${safeSuffix}` : ""}.xlsx`;
};

// Helper to format month-year for filenames, e.g. "March-2026"
const formatMonthYearForReports = (year, monthNum) => {
  if (!year || !monthNum) return "";
  const monthIndex = Number(monthNum) - 1;
  if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return `${year}`;
  const monthName = new Date(year, monthIndex, 1).toLocaleString("default", { month: "long" });
  return `${monthName}-${year}`;
};

// Helper to format current date as DD-MM-YYYY for non-month based reports
const getTodayDdMmYyyy = () => {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const y = d.getFullYear();
  return `${day}-${m}-${y}`;
};

// Helper function to convert number to words (Indian format)
const numberToWords = (num) => {
  if (!num || num === 0) return "";
  
  const numValue = parseFloat(num);
  if (isNaN(numValue) || numValue < 0) return "";
  
  // Convert to integer for word conversion
  const integerPart = Math.floor(numValue);
  
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];
  
  const convertHundreds = (n) => {
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result.trim();
  };
  
  if (integerPart === 0) return "";
  
  let result = "";
  let remaining = integerPart;
  
  // Crores
  if (remaining >= 10000000) {
    const crores = Math.floor(remaining / 10000000);
    result += convertHundreds(crores) + " Crore ";
    remaining %= 10000000;
  }
  
  // Lakhs
  if (remaining >= 100000) {
    const lakhs = Math.floor(remaining / 100000);
    result += convertHundreds(lakhs) + " Lakh ";
    remaining %= 100000;
  }
  
  // Thousands
  if (remaining >= 1000) {
    const thousands = Math.floor(remaining / 1000);
    result += convertHundreds(thousands) + " Thousand ";
    remaining %= 1000;
  }
  
  // Hundreds, Tens, Ones
  if (remaining > 0) {
    result += convertHundreds(remaining);
  }
  
  return result.trim();
};

// Helper function to calculate percentage change
const calculatePercentageChange = (current, revised) => {
  if (!current || !revised || current === 0) return null;
  const currentNum = parseFloat(current);
  const revisedNum = parseFloat(revised);
  if (isNaN(currentNum) || isNaN(revisedNum)) return null;
  
  const change = ((revisedNum - currentNum) / currentNum) * 100;
  return change;
};

// Age at a given date (e.g. DOJ Age = age at date of joining)
const getAgeAtDate = (birthDate, atDate) => {
  if (!birthDate || !atDate) return "-";
  const birth = new Date(birthDate);
  const at = new Date(atDate);
  let age = at.getFullYear() - birth.getFullYear();
  const m = at.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : "-";
};

// Service duration from start date to end date (default end = today)
const getServiceYearsMonths = (startDate, endDate = new Date()) => {
  if (!startDate) return { years: "-", months: "-" };
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (end < start) return { years: 0, months: 0 };
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  if (months < 0) {
    years--;
    months += 12;
  }
  if (end.getDate() < start.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }
  return { years, months };
};

/**
 * A comprehensive Reports page that displays and manages:
 * 1. Employee Reports (New joinee, Left employee, Age report, Service report)
 * 2. Attendance Reports
 * 3. Payroll Reports (Payslips, Form-16)
 */
const Reports = memo(
  function Reports({
    darkMode,
    setCurrentPage,
    toggleDarkMode,
    scope,
    forcedSubTab = null,
    onSubTabChange = null,
    hideSidebar = false,
    canDownload = true,
  }) {
    const isEmployeeOnly = scope === "employee";
    const [activeTab, setActiveTab] = useState("employees");
    const [activeSubTab, setActiveSubTab] = useState(forcedSubTab || "employees");
    // const [employees, setEmployees] = useState([]); // Removed as we don't fetch all anymore
    const [selectedYear, setSelectedYear] = useState("2024-25");
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [showEmployeeList, setShowEmployeeList] = useState(false);
    const [loadedEmployees, setLoadedEmployees] = useState([]);
    
    // For Filters component integration
    const [showFilters, setShowFilters] = useState(false);
    const [advancedFiltersState, setAdvancedFiltersState] = useState({});
    const [advancedFiltersParams, setAdvancedFiltersParams] = useState({});
    
    // For Salary Revision Report
    const [selectedMonth, setSelectedMonth] = useState("");
    const [salaryRevisionReportData, setSalaryRevisionReportData] = useState([]);

    // For Employee Reports - month selection (replaces Load Data)
    const [employeeReportMonth, setEmployeeReportMonth] = useState(""); // used for salary-report only
    const [confirmationReportMonth, setConfirmationReportMonth] = useState(""); // per-tab: only call API when user selects month on Confirmation
    const [confirmationReportCount, setConfirmationReportCount] = useState(null); // from backend summary (matches export count)
    const [confirmationSummaryLoading, setConfirmationSummaryLoading] = useState(false);
    const [transfersReportMonth, setTransfersReportMonth] = useState(""); // per-tab: only call API when user selects month on Transfers
    const [resignationsReportMonth, setResignationsReportMonth] = useState(""); // per-tab: only call API when user selects month on Resignations
    // Count from backend for transfers report (real data being exported)
    const [transfersReportCount, setTransfersReportCount] = useState(null);
    const [transfersSummaryLoading, setTransfersSummaryLoading] = useState(false);
    // Count from backend for resignations report (real data being exported)
    const [resignationsReportCount, setResignationsReportCount] = useState(null);
    const [resignationsSummaryLoading, setResignationsSummaryLoading] = useState(false);
    // Count from backend for salary report (real data being exported)
    const [salaryReportCount, setSalaryReportCount] = useState(null);
    const [salarySummaryLoading, setSalarySummaryLoading] = useState(false);

    // When embedded inside Employee module, show only Employee Reports
    useEffect(() => {
      if (!isEmployeeOnly) return;
      if (activeTab !== "employees") setActiveTab("employees");
      // If something else set subtab (e.g. attendance/payroll), reset to employee report list
      const allowed = new Set([
        "employees",
        "age-report",
        "service-report",
        "salary-report",
        "confirmation",
        "transfers",
        "salary-revision",
        "resignations",
      ]);
      if (!allowed.has(activeSubTab)) setActiveSubTab("employees");
    }, [isEmployeeOnly, activeTab, activeSubTab]);

    useEffect(() => {
      if (!forcedSubTab) return;
      setActiveTab("employees");
      setActiveSubTab(forcedSubTab);
    }, [forcedSubTab]);

    useEffect(() => {
      if (typeof onSubTabChange === "function") onSubTabChange(activeSubTab);
    }, [activeSubTab, onSubTabChange]);

    // Pagination state
    const [currentPage, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalEmployees, setTotalEmployees] = useState(0);

    // Master data for filters
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [grades, setGrades] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);

    // Default filter shape for every report (each report has its own copy)
    const getDefaultFilters = () => ({
      branch: "",
      department: "",
      designation: "",
      grade: "",
      category: "",
      subDepartment: "",
      employmentType: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      reportDateFrom: "",
      reportDateTo: "",
      dateFilterType: "all",
      pfApplicable: "",
      esiApplicable: "",
      ptApplicable: "",
      gratuityApplicable: "",
      epsApplicable: "",
      ceilingPf: "",
      hasUan: "",
      hasEsiIp: "",
      ctcFrom: "",
      ctcTo: "",
      search: "",
      gender: "",
      userStatus: "",
      bloodGroup: "",
      reportingManagerId: "",
    });

    const REPORT_IDS = ["employees", "age-report", "service-report", "salary-report", "confirmation", "transfers", "salary-revision", "resignations"];

    // Per-report filter state (one filter state per report; switching tabs resets that tab's filters)
    const [reportFilters, setReportFilters] = useState(() => {
      const d = getDefaultFilters();
      return REPORT_IDS.reduce((acc, id) => ({ ...acc, [id]: { ...d } }), {});
    });

    // Current report's filters (derived)
    const currentFilters = reportFilters[activeSubTab] ?? getDefaultFilters();

    const hasActiveFiltersForReport = useMemo(() => {
      const f = currentFilters;
      return !!(
        f.branch || f.department || f.designation || f.grade || f.category || f.subDepartment ||
        f.employmentType || f.status || f.search || f.gender || f.userStatus || f.bloodGroup || f.reportingManagerId ||
        (f.dateFilterType && f.dateFilterType !== "all") || f.reportDateFrom || f.reportDateTo ||
        f.pfApplicable || f.esiApplicable || f.ptApplicable || f.gratuityApplicable || f.epsApplicable ||
        f.ceilingPf || f.hasUan || f.hasEsiIp || f.ctcFrom || f.ctcTo
      );
    }, [currentFilters]);

    const [employeesReportCount, setEmployeesReportCount] = useState(null);
    const [employeesReportSummaryLoading, setEmployeesReportSummaryLoading] = useState(false);
    const [ageReportCount, setAgeReportCount] = useState(null);
    const [ageReportSummaryLoading, setAgeReportSummaryLoading] = useState(false);
    const [serviceReportCount, setServiceReportCount] = useState(null);
    const [serviceReportSummaryLoading, setServiceReportSummaryLoading] = useState(false);
    const [reportingManagers, setReportingManagers] = useState([]);

    const [loading, setLoading] = useState(false);
    const [lopReversalReportMonth, setLOPReversalReportMonth] = useState(null);
    const [lopReversalReportYear, setLOPReversalReportYear] = useState(null);
    const [lopReversalReportData, setLOPReversalReportData] = useState([]);
    const [lopReversalReportLoading, setLOPReversalReportLoading] = useState(false);
    const [lopReversalReportFetched, setLOPReversalReportFetched] = useState(false);
    const [lopReversalMinDate, setLOPReversalMinDate] = useState(null);
    const companyMinMonth = useMemo(() => resolveCompanyMinMonthValue() || undefined, []);
    const lopReversalFetchSeqRef = useRef(0);

    const parseYearMonthFromPickerValue = (raw) => {
      const s = String(raw || "").trim();
      const m = s.match(/^(\d{4})-(\d{2})/);
      if (!m) return null;
      const year = Number(m[1]);
      const month = Number(m[2]);
      if (!year || month < 1 || month > 12) return null;
      return { year, month };
    };

    const loadLOPReversalReport = useCallback(async (month, year) => {
      const fetchSeq = ++lopReversalFetchSeqRef.current;
      if (!attendanceService || typeof attendanceService.getLOPReversalReport !== "function") {
        toast.error("LOP Reversal report is not available.");
        setLOPReversalReportFetched(true);
        setLOPReversalReportData([]);
        return;
      }
      setLOPReversalReportLoading(true);
      setLOPReversalReportFetched(false);
      try {
        const res = await attendanceService.getLOPReversalReport(month, year);
        if (fetchSeq !== lopReversalFetchSeqRef.current) return;
        const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setLOPReversalReportData(arr);
      } catch (err) {
        if (fetchSeq !== lopReversalFetchSeqRef.current) return;
        console.error(err);
        setLOPReversalReportData([]);
        toast.error("Failed to load LOP reversal report.");
      } finally {
        if (fetchSeq === lopReversalFetchSeqRef.current) {
          setLOPReversalReportLoading(false);
          setLOPReversalReportFetched(true);
        }
      }
    }, []);

    const prevLopAttendanceSubTabRef = useRef(activeSubTab);
    useEffect(() => {
      if (activeTab !== "attendance") {
        prevLopAttendanceSubTabRef.current = activeSubTab;
        return;
      }
      const prev = prevLopAttendanceSubTabRef.current;
      prevLopAttendanceSubTabRef.current = activeSubTab;
      if (activeSubTab === "lop-reversal-report" && prev !== "lop-reversal-report") {
        lopReversalFetchSeqRef.current += 1;
        setLOPReversalReportMonth(null);
        setLOPReversalReportYear(null);
        setLOPReversalReportData([]);
        setLOPReversalReportFetched(false);
        setLOPReversalReportLoading(false);
      }
    }, [activeTab, activeSubTab]);

    useEffect(() => {
      if (activeTab !== "attendance" || activeSubTab !== "lop-reversal-report") return;
      if (lopReversalReportMonth == null || lopReversalReportYear == null) {
        lopReversalFetchSeqRef.current += 1;
        setLOPReversalReportData([]);
        setLOPReversalReportFetched(false);
        setLOPReversalReportLoading(false);
        return;
      }
      loadLOPReversalReport(lopReversalReportMonth, lopReversalReportYear);
    }, [activeTab, activeSubTab, lopReversalReportMonth, lopReversalReportYear, loadLOPReversalReport]);

    // Keep month-year picker minimum same as Attendance (company service commencement date).
    useEffect(() => {
      try {
        const raw = localStorage.getItem("companyData");
        if (!raw) return;
        const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
        const commencesOn = companyData?.serviceCommencesOn;
        if (!commencesOn) return;
        const d = new Date(commencesOn);
        if (!isNaN(d.getTime())) setLOPReversalMinDate(d);
      } catch (e) {
        console.warn("Reports minDate from companyData.serviceCommencesOn:", e);
      }
    }, []);

    useEffect(() => {
      if (!lopReversalMinDate) return;
      if (lopReversalReportMonth == null || lopReversalReportYear == null) return;
      const minYear = lopReversalMinDate.getFullYear();
      const minMonth = lopReversalMinDate.getMonth() + 1;
      if (
        lopReversalReportYear < minYear ||
        (lopReversalReportYear === minYear && lopReversalReportMonth < minMonth)
      ) {
        setLOPReversalReportYear(minYear);
        setLOPReversalReportMonth(minMonth);
      }
    }, [lopReversalMinDate, lopReversalReportMonth, lopReversalReportYear]);

    // When switching to a report tab: reset that report's filters and its count (so coming back = fresh state, no ripple)
    const prevReportTabRef = useRef(activeSubTab);
    useEffect(() => {
      if (prevReportTabRef.current === activeSubTab) return;
      prevReportTabRef.current = activeSubTab;
      setReportFilters((prev) => ({ ...prev, [activeSubTab]: getDefaultFilters() }));
      // Clear all per-report month selections when switching between reports
      setEmployeeReportMonth("");
      setConfirmationReportMonth("");
      setTransfersReportMonth("");
      setResignationsReportMonth("");
      setSelectedMonth("");
      setShowEmployeeList(false);
      setLoadedEmployees([]);
      setSalaryRevisionReportData([]);
      setTransfersReportCount(null);
      setTransfersSummaryLoading(false);
      setResignationsReportCount(null);
      setResignationsSummaryLoading(false);
      setSalaryReportCount(null);
      setSalarySummaryLoading(false);
      setConfirmationReportCount(null);
      setConfirmationSummaryLoading(false);
      setEmployeesReportCount(null);
      setAgeReportCount(null);
      setServiceReportCount(null);
    }, [activeSubTab]);

    useEffect(() => {
      fetchMasterData();
      // fetchEmployees(); // Don't fetch employees on mount
    }, []);

    const fetchMasterData = async () => {
      try {
        setLoading(true);
        const [
          branchesRes,
          departmentsRes,
          designationsRes,
          gradesRes,
          categoriesRes,
          subDepartmentsRes,
          reportingManagersRes,
        ] = await Promise.all([
          branchService.getBranches(),
          departmentService.getDepartments(),
          designationService.getDesignations(),
          gradesService.getGrades(),
          categoryService.getAllCategories(),
          subDepartmentService.getAllSubDepartments(),
          employeeService.getReportingEmployees ? employeeService.getReportingEmployees() : Promise.resolve({ data: [] }),
        ]);

        if (branchesRes.success) setBranches(branchesRes.data);
        if (departmentsRes.success) setDepartments(departmentsRes.data);
        if (designationsRes.success) setDesignations(designationsRes.data);
        if (gradesRes.success) setGrades(gradesRes.data);
        if (categoriesRes.success) setCategories(categoriesRes.data);
        if (subDepartmentsRes.success)
          setSubDepartments(subDepartmentsRes.data);
        if (reportingManagersRes?.data) setReportingManagers(Array.isArray(reportingManagersRes.data) ? reportingManagersRes.data : []);
      } catch (error) {
        console.error("Error fetching master data:", error);
        toast.error("Failed to fetch master data");
      } finally {
        setLoading(false);
      }
    };

    // Replaced fetchEmployees with fetchReportData
    const fetchReportData = async (page = 1, isExport = false) => {
      try {
        setLoading(true);

        // Prepare filters - for employee reports, fetch all in one page for display & export
        const isEmployeeReport = activeSubTab !== "salary-revision";
        const queryParams = {
          page: isEmployeeReport ? 1 : page,
          limit: isExport || isEmployeeReport ? 10000 : itemsPerPage,
          ...currentFilters,
          activeSubTab, // Pass the active sub-tab for backend filtering (e.g. confirmation/resignations in selected month)
        };
        // If report month is selected, pass month/year for filtering (confirmation uses its own month state)
        const monthForFetch = activeSubTab === "confirmation" ? confirmationReportMonth : employeeReportMonth;
        if (monthForFetch) {
          const [year, month] = monthForFetch.split("-");
          queryParams.month = parseInt(month, 10);
          queryParams.year = parseInt(year, 10);
        }
        // Confirmation and resignations reports: only employees confirmed/resigned in selected month, order by DOJ desc
        if (activeSubTab === "confirmation" || activeSubTab === "resignations") {
          queryParams.sortBy = "dateOfJoin";
          queryParams.sortOrder = "desc";
        }

        const response = await employeeService.getEmployees(queryParams);

        if (response.success) {
          if (isExport) {
            return response.data;
          } else {
            setLoadedEmployees(response.data || []);
            setTotalPages(response.pagination?.totalPages || 1);
            setTotalEmployees(response.pagination?.totalItems || 0);
            setPage(page);
            setShowEmployeeList(true);
            const reportTabsWithSummary = ["employees", "age-report", "service-report", "salary-report", "confirmation", "transfers", "resignations"];
            if (reportTabsWithSummary.includes(activeSubTab)) {
              toast.success("Summary loaded for the selected date range.");
            }
          }
        } else {
          if (!isExport) setLoadedEmployees([]);
        }
        return isExport ? [] : null;
      } catch (error) {
        console.error("Error fetching report data:", error);
        toast.error("Failed to fetch report data");
        return isExport ? [] : null;
      } finally {
        setLoading(false);
      }
    };

    // Get the relevant date for each report type
    const getEmployeeDateForReport = (employee, reportType) => {
      switch (reportType) {
        case "employees":
          // Use the selected date filter type
          switch (currentFilters.dateFilterType) {
            case "all":
              return null; // No specific date filtering when 'all' is selected
            case "dateOfJoin":
              return (
                employee.dateOfJoin || employee.joiningDate || employee.joinDate
              );
            case "dateOfRelieving":
              return employee.dateOfRelieving || employee.relievedDate;
            case "confirmationDate":
              return employee.confirmationDate;
            case "resignationDate":
              return employee.resignationDate;
            case "terminationDate":
              return employee.terminationDate;
            case "dateOfBirth":
              return employee.user?.dateOfBirth || employee.dateOfBirth;
            default:
              return (
                employee.dateOfJoin || employee.joiningDate || employee.joinDate
              );
          }
        case "confirmation":
          return employee.confirmationDate;
        case "transfers":
          return employee.transferDate;
        case "salary-revision":
          return employee.salaryRevisionDate;
        case "resignations":
          return employee.resignationDate;
        default:
          return (
            employee.dateOfJoin || employee.joiningDate || employee.joinDate
          );
      }
    };

    // Get the date label for each report type
    const getReportDateLabel = (reportType) => {
      switch (reportType) {
        case "employees":
          // Use the selected date filter type
          switch (currentFilters.dateFilterType) {
            case "all":
              return "All Dates";
            case "dateOfJoin":
              return "Date of Joining";
            case "dateOfRelieving":
              return "Date of Relieving";
            case "confirmationDate":
              return "Confirmation Date";
            case "resignationDate":
              return "Resignation Date";
            case "terminationDate":
              return "Termination Date";
            case "dateOfBirth":
              return "Date of Birth";
            default:
              return "Date of Joining";
          }
        case "confirmation":
          return "Confirmation Date";
        case "transfers":
          return "Transfer Date";
        case "salary-revision":
          return "Action Date";
        case "resignations":
          return "Resignation Date";
        case "age-report":
          return "DOJ";
        default:
          return "Date";
      }
    };

    const handleFilterChange = (key, value) => {
      setReportFilters((prev) => ({
        ...prev,
        [activeSubTab]: { ...(prev[activeSubTab] ?? getDefaultFilters()), [key]: value },
      }));
    };

    const clearCurrentReportCount = () => {
      if (activeSubTab === "employees") setEmployeesReportCount(null);
      if (activeSubTab === "age-report") setAgeReportCount(null);
      if (activeSubTab === "service-report") setServiceReportCount(null);
      if (activeSubTab === "salary-report") setSalaryReportCount(null);
      if (activeSubTab === "confirmation") setConfirmationReportCount(null);
      if (activeSubTab === "transfers") setTransfersReportCount(null);
      if (activeSubTab === "resignations") setResignationsReportCount(null);
    };

    const clearFilters = () => {
      setReportFilters((prev) => ({ ...prev, [activeSubTab]: getDefaultFilters() }));
      clearCurrentReportCount();
    };

    // Build API params from any report's filter state (same shape for all reports)
    const buildReportExportParams = (filtersObj = currentFilters) => {
      const f = filtersObj || currentFilters;
      const p = {};
      if (f.branch) p.branchId = f.branch;
      if (f.department) p.departmentId = f.department;
      if (f.designation) p.designationId = f.designation;
      if (f.grade) p.gradeId = f.grade;
      if (f.category) p.categoryId = f.category;
      if (f.subDepartment) p.subDepartmentId = f.subDepartment;
      if (f.reportingManagerId) p.reportingManagerId = f.reportingManagerId;
      if (f.employmentType) p.employmentType = f.employmentType;
      if (f.status) p.employmentStatus = f.status;
      if (f.dateFilterType && f.dateFilterType !== "all") {
        p.dateFilterType = f.dateFilterType;
        if (f.reportDateFrom) p.dateFrom = f.reportDateFrom;
        if (f.reportDateTo) p.dateTo = f.reportDateTo;
      }
      if (f.search && String(f.search).trim()) p.search = f.search.trim();
      if (f.gender) p.gender = f.gender;
      if (f.userStatus) p.userStatus = f.userStatus;
      if (f.bloodGroup) p.bloodGroup = f.bloodGroup;
      if (f.pfApplicable === "true" || f.pfApplicable === "false") p.pfApplicable = f.pfApplicable;
      if (f.epsApplicable === "true" || f.epsApplicable === "false") p.epsApplicable = f.epsApplicable;
      if (f.esiApplicable === "true" || f.esiApplicable === "false") p.esiApplicable = f.esiApplicable;
      if (f.ceilingPf === "true" || f.ceilingPf === "false") p.ceilingPf = f.ceilingPf;
      if (f.hasUan === "true" || f.hasUan === "false") p.hasUAN = f.hasUan;
      if (f.hasEsiIp === "true" || f.hasEsiIp === "false") p.hasESIIP = f.hasEsiIp;
      if (f.ptApplicable === "true" || f.ptApplicable === "false") p.ptApplicable = f.ptApplicable;
      if (f.gratuityApplicable === "true" || f.gratuityApplicable === "false") p.gratuityApplicable = f.gratuityApplicable;
      if (f.ctcFrom !== "" && f.ctcFrom != null) p.ctcFrom = f.ctcFrom;
      if (f.ctcTo !== "" && f.ctcTo != null) p.ctcTo = f.ctcTo;
      p.sortBy = "dateOfJoin";
      p.sortOrder = "desc";
      return p;
    };

    // Fetch Salary Revision Report data based on selected month
    const fetchSalaryRevisionReport = async (month = selectedMonth) => {
      if (!month) {
        toast.error("Please select a month");
        return;
      }

      try {
        setLoading(true);
        // Parse month (YYYY-MM format)
        const [year, monthNum] = month.split('-');
        if (!year || !monthNum) {
          toast.error("Invalid month format");
          setLoading(false);
          return;
        }

        // Build query params with filters if they exist
        const queryParams = {
          limit: 10000,
          ...advancedFiltersParams,
        };

        // Fetch employees with filters applied
        const employeesResponse = await employeeService.getEmployees(queryParams);

        if (employeesResponse.success && employeesResponse.data && Array.isArray(employeesResponse.data)) {
          const employees = employeesResponse.data.filter(emp => emp && emp.id);
          
          if (employees.length === 0) {
            setSalaryRevisionReportData([]);
            setLoadedEmployees([]);
            setTotalEmployees(0);
            setTotalPages(1);
            toast.info("No employees found matching the filters");
            setLoading(false);
            setShowEmployeeList(true);
            return;
          }

          // OPTIMIZATION: Fetch all salary histories in parallel using Promise.all
          const salaryHistoryPromises = employees.map(employee => 
            salaryService.getSalaryHistory(employee.id).catch(error => {
              console.error(`Error fetching salary history for employee ${employee.id}:`, error);
              return { success: false, data: [] };
            })
          );

          const salaryHistoryResponses = await Promise.all(salaryHistoryPromises);

          const reportData = [];
          
          // Process all salary histories
          salaryHistoryResponses.forEach((salaryHistoryResponse, idx) => {
            const employee = employees[idx];
            if (!employee) return;

            if (salaryHistoryResponse && salaryHistoryResponse.success && salaryHistoryResponse.data && Array.isArray(salaryHistoryResponse.data)) {
              // Sort by action date (fallback createdAt / effective) descending
              const sortedHistory = [...salaryHistoryResponse.data].sort((a, b) => {
                const dateA = new Date(a.actionDate || a.createdAt || a.effectiveDate || 0);
                const dateB = new Date(b.actionDate || b.createdAt || b.effectiveDate || 0);
                return dateB - dateA;
              });

              // Filter by Action Date month (reports); fall back to Increment Effective Date
              sortedHistory.forEach((revision, revIndex) => {
                if (!revision) return;
                const filterDateRaw =
                  revision.actionDate || revision.createdAt || revision.effectiveDate;
                if (!filterDateRaw) return;
                
                try {
                  const filterDate = new Date(filterDateRaw);
                  if (isNaN(filterDate.getTime())) return;
                  const revYear = filterDate.getFullYear();
                  const revMonth = String(filterDate.getMonth() + 1).padStart(2, '0');
                  
                  if (`${revYear}-${revMonth}` === month) {
                    // Get previous CTC for percentage calculation (same logic as Employee.jsx)
                    const previousCtc = revIndex < sortedHistory.length - 1 
                      ? (sortedHistory[revIndex + 1]?.ctcAmount || sortedHistory[revIndex + 1]?.newCtc || sortedHistory[revIndex + 1]?.previousCtc)
                      : (revision.previousCtc || employee.ctc || 0);
                    
                    // Use ctcAmount or newCtc depending on what the API returns
                    const currentCtc = revision.ctcAmount || revision.newCtc || 0;
                    
                    reportData.push({
                      ...revision,
                      ctcAmount: currentCtc, // Normalize to ctcAmount
                      previousCtc: revision.previousCtc ?? previousCtc,
                      employee: employee,
                    });
                  }
                } catch (e) {
                  console.error("Error parsing date:", filterDateRaw, e);
                }
              });
            }
          });

          setSalaryRevisionReportData(reportData);
          setLoadedEmployees(reportData);
          setTotalEmployees(reportData.length);
          setTotalPages(1);
          if (reportData.length > 0) {
            toast.success("Summary loaded for the selected date range.");
          }
          if (reportData.length === 0) {
            // Format month name for display
            const [year, monthNum] = month.split('-');
            const monthNames = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            const monthName = monthNames[parseInt(monthNum) - 1];
            toast(`No salary revisions found for ${monthName}-${year}`, {
              icon: 'ℹ️',
              duration: 4000,
            });
          }
        } else {
          setSalaryRevisionReportData([]);
          setLoadedEmployees([]);
          setShowEmployeeList(false);
          toast.error("Failed to fetch employees");
        }
      } catch (error) {
        console.error("Error fetching salary revision report:", error);
        toast.error("Failed to fetch salary revision report: " + (error.message || "Unknown error"));
        setSalaryRevisionReportData([]);
        setLoadedEmployees([]);
        setShowEmployeeList(false);
      } finally {
        // Only set loading to false and show employee list after all data processing is complete
        setLoading(false);
        setShowEmployeeList(true);
      }
    };

    // Auto-load when month changes for salary-revision tab
    useEffect(() => {
      if (activeSubTab === "salary-revision" && selectedMonth) {
        // Clear previous data and show loading before fetching
        setShowEmployeeList(false);
        // Auto-load when month is selected
        fetchSalaryRevisionReport(selectedMonth);
      } else if (activeSubTab !== "salary-revision") {
        // Clear data when switching away from salary-revision tab
        setSalaryRevisionReportData([]);
        setShowEmployeeList(false);
        setLoadedEmployees([]);
      } else if (activeSubTab === "salary-revision" && !selectedMonth) {
        // Clear data when no month is selected
        setSalaryRevisionReportData([]);
        setLoadedEmployees([]);
        setShowEmployeeList(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubTab, selectedMonth]);

    // Auto-load when month is selected for employee reports (except Employees/Age/Service which use From/To + Load)
    const isDateRangeReport = [""].includes(activeSubTab);
    useEffect(() => {
      if (activeSubTab === "salary-revision") return;
      if (isDateRangeReport) {
        setShowEmployeeList(false);
        setLoadedEmployees([]);
        return;
      }
      if (activeSubTab === "transfers" && transfersReportMonth) {
        setShowEmployeeList(false);
        setTransfersReportCount(null);
        setTransfersSummaryLoading(true);
        const [y, m] = transfersReportMonth.split("-");
        const monthNum = parseInt(m, 10);
        const yearNum = parseInt(y, 10);
        employeeService
          .getTransfersReportSummary(monthNum, yearNum)
          .then((res) => {
            if (res?.data?.success) {
              setTransfersReportCount(res.data.count ?? 0);
              setShowEmployeeList(true);
            }
          })
          .catch((err) => {
            console.error("Failed to fetch transfers report summary:", err);
            toast.error("Failed to load transfers count");
            setTransfersReportCount(0);
            setShowEmployeeList(true);
          })
          .finally(() => setTransfersSummaryLoading(false));
        return;
      }
      if (activeSubTab === "resignations" && resignationsReportMonth) {
        setShowEmployeeList(false);
        setResignationsReportCount(null);
        setResignationsSummaryLoading(true);
        const [y, m] = resignationsReportMonth.split("-");
        const monthNum = parseInt(m, 10);
        const yearNum = parseInt(y, 10);
        employeeService
          .getResignationsReportSummary(monthNum, yearNum)
          .then((res) => {
            if (res?.data?.success) {
              setResignationsReportCount(res.data.count ?? 0);
              setShowEmployeeList(true);
            }
          })
          .catch((err) => {
            console.error("Failed to fetch resignations report summary:", err);
            toast.error("Failed to load resignations count");
            setResignationsReportCount(0);
            setShowEmployeeList(true);
          })
          .finally(() => setResignationsSummaryLoading(false));
        return;
      }
      if (activeSubTab === "salary-report" && employeeReportMonth) {
        setShowEmployeeList(false);
        setSalaryReportCount(null);
        setSalarySummaryLoading(true);
        const [y, m] = employeeReportMonth.split("-");
        const monthNum = parseInt(m, 10);
        const yearNum = parseInt(y, 10);
        employeeService
          .getSalaryReportSummary(monthNum, yearNum)
          .then((res) => {
            if (res?.data?.success) {
              setSalaryReportCount(res.data.count ?? 0);
              setShowEmployeeList(true);
            }
          })
          .catch((err) => {
            console.error("Failed to fetch salary report summary:", err);
            toast.error("Failed to load salary report count");
            setSalaryReportCount(0);
            setShowEmployeeList(true);
          })
          .finally(() => setSalarySummaryLoading(false));
        return;
      }
      if (activeSubTab === "confirmation" && confirmationReportMonth) {
        setShowEmployeeList(false);
        setConfirmationReportCount(null);
        setConfirmationSummaryLoading(true);
        const [y, m] = confirmationReportMonth.split("-");
        const monthNum = parseInt(m, 10);
        const yearNum = parseInt(y, 10);
        employeeService
          .getConfirmationReportSummary(monthNum, yearNum)
          .then((res) => {
            if (res?.data?.success) {
              setConfirmationReportCount(res.data.count ?? 0);
              setShowEmployeeList(true);
            }
          })
          .catch((err) => {
            console.error("Failed to fetch confirmation report summary:", err);
            toast.error("Failed to load confirmation count");
            setConfirmationReportCount(0);
            setShowEmployeeList(true);
          })
          .finally(() => setConfirmationSummaryLoading(false));
        return;
      }
      {
        setShowEmployeeList(false);
        if (activeSubTab !== "confirmation") setLoadedEmployees([]);
        if (activeSubTab === "transfers") {
          setTransfersReportCount(null);
          setTransfersSummaryLoading(false);
        }
        if (activeSubTab === "resignations") {
          setResignationsReportCount(null);
          setResignationsSummaryLoading(false);
        }
        if (activeSubTab === "salary-report") {
          setSalaryReportCount(null);
          setSalarySummaryLoading(false);
        }
        if (activeSubTab === "confirmation") {
          setConfirmationReportCount(null);
          setConfirmationSummaryLoading(false);
          if (!confirmationReportMonth) setLoadedEmployees([]);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubTab, employeeReportMonth, confirmationReportMonth, transfersReportMonth, resignationsReportMonth]);

    const handlePageChange = (newPage) => {
      fetchReportData(newPage);
    };

    const exportReport = async (reportType) => {
      const downloadOnlyReports = ["salary-report", "confirmation", "transfers", "salary-revision", "resignations"];

      // Salary Revision: require month selected; data is loaded when month is selected
      if (activeSubTab === "salary-revision") {
        if (!selectedMonth) {
          toast.error("Please select a month first");
          return;
        }
        if (!salaryRevisionReportData || salaryRevisionReportData.length === 0) {
          toast.error("No data to export for the selected month");
          return;
        }
      } else if (downloadOnlyReports.includes(activeSubTab)) {
        // Salary Report, Confirmation, Transfers, Resignations: require month selected (per-tab)
        const monthForTab = activeSubTab === "transfers" ? transfersReportMonth : activeSubTab === "resignations" ? resignationsReportMonth : activeSubTab === "confirmation" ? confirmationReportMonth : employeeReportMonth;
        if (!monthForTab) {
          toast.error("Please select a month first");
          return;
        }
      } else if (activeSubTab === "lop-reversal-report") {
        if (lopReversalReportMonth == null || lopReversalReportYear == null) {
          toast.error("Select a payroll month to export.");
          return;
        }
        if (!lopReversalReportData || lopReversalReportData.length === 0) {
          toast.error("No LOP reversal data to export for the selected payroll month.");
          return;
        }
      } else {
        // Employees, Age, Service: no Load button; Download will fetch with current filters if needed
      }

      try {
        if (activeSubTab === "lop-reversal-report") {
          const companyCode = getCompanyCodeForReports();
          const monthYearLabel = lopReversalReportMonth
            ? formatMonthYearForReports(lopReversalReportYear, lopReversalReportMonth)
            : "";
          const wsData = [
            ["Sl No", "Employee Code", "Employee Name", "DOJ", "LOP Month", "LOP Reversal Days", "Dates", "Payroll Month"],
            ...lopReversalReportData.map((r, idx) => [
              idx + 1,
              r.employeeCode,
              r.employeeName,
              r.doj ? String(r.doj).slice(0, 10) : "",
              r.lopMonth,
              r.lopReversalDays,
              r.dates,
              r.payrollMonth,
            ]),
          ];
          const ws = XLSX.utils.aoa_to_sheet(wsData);
          // Auto-size columns based on content length
          ws["!cols"] = wsData[0].map((_, colIdx) => {
            const maxLen = wsData.reduce((m, row) => {
              const val = row[colIdx] == null ? "" : String(row[colIdx]);
              return Math.max(m, val.length);
            }, 0);
            return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
          });
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "LOP Reversal Report");
          const suffix = monthYearLabel || getTodayDdMmYyyy();
          const filename = buildReportFilename(companyCode, "LOP Reversal Report", suffix);
          XLSX.writeFile(wb, filename);
          toast.success("LOP Reversal report exported.");
          return;
        }

        // Employees report: export via same API as Employee module with all filters (DD/MM/YYYY in Excel)
        if (activeSubTab === "employees") {
          const params = buildReportExportParams();
          const response = await employeeService.exportEmployees(params);
          if (!response || !response.data || response.status !== 200) {
            toast.error("Export failed. No file received.");
            return;
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const todayLabel = getTodayDdMmYyyy();
          a.download = buildReportFilename(companyCode, "Employees Report", todayLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Employees report exported successfully.");
          return;
        }

        // Confirmation and resignations: export from backend (filter by month, DOJ desc)
        if (activeSubTab === "salary-report" && employeeReportMonth) {
          const [y, m] = employeeReportMonth.split("-");
          const monthNum = parseInt(m, 10);
          const yearNum = parseInt(y, 10);
          const response = await employeeService.exportSalaryReport(monthNum, yearNum);
          if (!response?.data) {
            toast.error("Export failed. No file received.");
            return;
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const monthYearLabel = formatMonthYearForReports(yearNum, monthNum);
          a.download = buildReportFilename(companyCode, "Salary Report", monthYearLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Salary report exported.");
          return;
        }
        if (activeSubTab === "confirmation" && confirmationReportMonth) {
          const [y, m] = confirmationReportMonth.split("-");
          const monthNum = parseInt(m, 10);
          const yearNum = parseInt(y, 10);
          const response = await employeeService.exportConfirmationReport(monthNum, yearNum);
          if (!response?.data) {
            toast.error("Export failed. No file received.");
            return;
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const monthYearLabel = formatMonthYearForReports(yearNum, monthNum);
          a.download = buildReportFilename(companyCode, "Confirmation Report", monthYearLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Confirmation report exported.");
          return;
        }
        if (activeSubTab === "transfers" && transfersReportMonth) {
          const [y, m] = transfersReportMonth.split("-");
          const monthNum = parseInt(m, 10);
          const yearNum = parseInt(y, 10);
          const response = await employeeService.exportTransfersReport(monthNum, yearNum);
          if (!response?.data) {
            toast.error("Export failed. No file received.");
            return;
          }

          const originalBlob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const arrayBuffer = await originalBlob.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });

          // Strip Excel repair / junk sheet names (e.g. "Recovered_Sheet1") — not the same as "recovery".
          const removeJunkSheetsFromWorkbook = (wb) => {
            if (!wb?.SheetNames?.length) return;
            const isJunkSheetName = (name) => {
              const raw = String(name ?? "").trim();
              if (!raw) return true;
              const norm = raw.toLowerCase().replace(/\s+/g, " ");
              if (norm.startsWith("recovered")) return true;
              if (norm.includes("recovered") && /sheet/i.test(norm)) return true;
              if (/^recovery\b/i.test(norm)) return true;
              if (/^recovered[_\s-]+sheet/i.test(raw)) return true;
              return false;
            };
            const toRemove = wb.SheetNames.filter(isJunkSheetName);
            for (const n of toRemove) delete wb.Sheets[n];
            if (toRemove.length) {
              wb.SheetNames = wb.SheetNames.filter((n) => !isJunkSheetName(n));
            }
          };

          removeJunkSheetsFromWorkbook(workbook);
          const branchSheetName = (workbook.SheetNames || []).find((n) => /branch/i.test(n));
          const departmentSheetName = (workbook.SheetNames || []).find(
            (n) => /department/i.test(n) && !/sub/i.test(n),
          );
          const subDepartmentSheetName = (workbook.SheetNames || []).find((n) => /sub-?\s*department/i.test(n));
          const designationSheetName = (workbook.SheetNames || []).find((n) => /designation/i.test(n));
          const gradeSheetName = (workbook.SheetNames || []).find((n) => /grade/i.test(n));
          const categorySheetName = (workbook.SheetNames || []).find((n) => /category/i.test(n));
          const sourceSheetName = branchSheetName || workbook.SheetNames?.[0];
          const sourceSheet = sourceSheetName ? workbook.Sheets[sourceSheetName] : null;

          // In Branch sheet, merge corresponding From/To pairs into final columns (keep all other columns)
          if (branchSheetName && sourceSheet) {
            const aoa = XLSX.utils.sheet_to_json(sourceSheet, { header: 1, defval: "" });
            const headers = (aoa?.[0] ?? []).map((h) => String(h ?? "").trim());

            const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedHeaders = headers.map(normalizeHeader);
            const headerIndex = new Map(normalizedHeaders.map((h, i) => [h, i]));

            const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const pickToFrom = (row, toIdx, fromIdx) => {
              const toVal = toIdx >= 0 ? row?.[toIdx] : "";
              const fromVal = fromIdx >= 0 ? row?.[fromIdx] : "";
              return hasValue(toVal) ? toVal : (fromVal ?? "");
            };

            const mergeSpecs = [
              { from: "From Department", to: "To Department", out: "Department" },
              { from: "From Sub-Department", to: "To Sub-Department", out: "Sub-Department" },
              { from: "From Designation", to: "To Designation", out: "Designation" },
              { from: "From Grade", to: "To Grade", out: "Grade" },
              { from: "From Category", to: "To Category", out: "Category" },
              { from: "From Reporting Manager", to: "To Reporting Manager", out: "Reporting Manager" },
            ].map((s) => ({
              ...s,
              fromKey: normalizeHeader(s.from),
              toKey: normalizeHeader(s.to),
            }));

            const indicesByOut = new Map(
              mergeSpecs.map((s) => {
                const fromIdx = headerIndex.has(s.fromKey) ? headerIndex.get(s.fromKey) : -1;
                const toIdx = headerIndex.has(s.toKey) ? headerIndex.get(s.toKey) : -1;
                return [s.out, { fromIdx, toIdx }];
              }),
            );

            const excludedIdx = new Set();
            for (const { fromIdx, toIdx } of indicesByOut.values()) {
              if (fromIdx >= 0) excludedIdx.add(fromIdx);
              if (toIdx >= 0) excludedIdx.add(toIdx);
            }

            const firstPosByOut = new Map(
              mergeSpecs.map((s) => {
                const { fromIdx, toIdx } = indicesByOut.get(s.out) || { fromIdx: -1, toIdx: -1 };
                const pos =
                  fromIdx >= 0 && toIdx >= 0 ? Math.min(fromIdx, toIdx) : (fromIdx >= 0 ? fromIdx : toIdx);
                return [s.out, typeof pos === "number" ? pos : -1];
              }),
            );

            const outHeaders = [];
            const headerPlan = []; // { kind: "orig", idx } | { kind: "merged", out }
            const insertedOut = new Set();

            for (let i = 0; i < headers.length; i++) {
              for (const spec of mergeSpecs) {
                const insertPos = firstPosByOut.get(spec.out);
                const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
                if (
                  insertPos === i &&
                  (fromIdx >= 0 || toIdx >= 0) &&
                  !insertedOut.has(spec.out)
                ) {
                  insertedOut.add(spec.out);
                  outHeaders.push(spec.out);
                  headerPlan.push({ kind: "merged", out: spec.out });
                }
              }

              if (!excludedIdx.has(i)) {
                outHeaders.push(headers[i]);
                headerPlan.push({ kind: "orig", idx: i });
              }
            }

            for (const spec of mergeSpecs) {
              const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
              if ((fromIdx >= 0 || toIdx >= 0) && !insertedOut.has(spec.out)) {
                insertedOut.add(spec.out);
                outHeaders.push(spec.out);
                headerPlan.push({ kind: "merged", out: spec.out });
              }
            }

            const outRows = aoa.slice(1).map((row) =>
              headerPlan.map((p) => {
                if (p.kind === "orig") return row?.[p.idx] ?? "";
                const { fromIdx, toIdx } = indicesByOut.get(p.out) || { fromIdx: -1, toIdx: -1 };
                return pickToFrom(row, toIdx, fromIdx);
              }),
            );

            workbook.Sheets[branchSheetName] = XLSX.utils.aoa_to_sheet([outHeaders, ...outRows]);
          }

          // In Department sheet, merge corresponding From/To pairs into final columns (keep all other columns)
          if (departmentSheetName && workbook.Sheets[departmentSheetName]) {
            const deptSheet = workbook.Sheets[departmentSheetName];
            const aoa = XLSX.utils.sheet_to_json(deptSheet, { header: 1, defval: "" });
            const headers = (aoa?.[0] ?? []).map((h) => String(h ?? "").trim());

            const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedHeaders = headers.map(normalizeHeader);
            const headerIndex = new Map(normalizedHeaders.map((h, i) => [h, i]));

            const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const pickToFrom = (row, toIdx, fromIdx) => {
              const toVal = toIdx >= 0 ? row?.[toIdx] : "";
              const fromVal = fromIdx >= 0 ? row?.[fromIdx] : "";
              return hasValue(toVal) ? toVal : (fromVal ?? "");
            };

            const mergeSpecs = [
              { from: "From Branch", to: "To Branch", out: "Branch" },
              { from: "From Sub-Department", to: "To Sub-Department", out: "Sub-Department" },
              { from: "From Designation", to: "To Designation", out: "Designation" },
              { from: "From Grade", to: "To Grade", out: "Grade" },
              { from: "From Category", to: "To Category", out: "Category" },
              { from: "From Reporting Manager", to: "To Reporting Manager", out: "Reporting Manager" },
            ].map((s) => ({
              ...s,
              fromKey: normalizeHeader(s.from),
              toKey: normalizeHeader(s.to),
            }));

            const indicesByOut = new Map(
              mergeSpecs.map((s) => {
                const fromIdx = headerIndex.has(s.fromKey) ? headerIndex.get(s.fromKey) : -1;
                const toIdx = headerIndex.has(s.toKey) ? headerIndex.get(s.toKey) : -1;
                return [s.out, { fromIdx, toIdx }];
              }),
            );

            const excludedIdx = new Set();
            for (const { fromIdx, toIdx } of indicesByOut.values()) {
              if (fromIdx >= 0) excludedIdx.add(fromIdx);
              if (toIdx >= 0) excludedIdx.add(toIdx);
            }

            const firstPosByOut = new Map(
              mergeSpecs.map((s) => {
                const { fromIdx, toIdx } = indicesByOut.get(s.out) || { fromIdx: -1, toIdx: -1 };
                const pos =
                  fromIdx >= 0 && toIdx >= 0 ? Math.min(fromIdx, toIdx) : (fromIdx >= 0 ? fromIdx : toIdx);
                return [s.out, typeof pos === "number" ? pos : -1];
              }),
            );

            const outHeaders = [];
            const headerPlan = [];
            const insertedOut = new Set();

            for (let i = 0; i < headers.length; i++) {
              for (const spec of mergeSpecs) {
                const insertPos = firstPosByOut.get(spec.out);
                const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
                if (
                  insertPos === i &&
                  (fromIdx >= 0 || toIdx >= 0) &&
                  !insertedOut.has(spec.out)
                ) {
                  insertedOut.add(spec.out);
                  outHeaders.push(spec.out);
                  headerPlan.push({ kind: "merged", out: spec.out });
                }
              }

              if (!excludedIdx.has(i)) {
                outHeaders.push(headers[i]);
                headerPlan.push({ kind: "orig", idx: i });
              }
            }

            for (const spec of mergeSpecs) {
              const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
              if ((fromIdx >= 0 || toIdx >= 0) && !insertedOut.has(spec.out)) {
                insertedOut.add(spec.out);
                outHeaders.push(spec.out);
                headerPlan.push({ kind: "merged", out: spec.out });
              }
            }

            const outRows = aoa.slice(1).map((row) =>
              headerPlan.map((p) => {
                if (p.kind === "orig") return row?.[p.idx] ?? "";
                const { fromIdx, toIdx } = indicesByOut.get(p.out) || { fromIdx: -1, toIdx: -1 };
                return pickToFrom(row, toIdx, fromIdx);
              }),
            );

            workbook.Sheets[departmentSheetName] = XLSX.utils.aoa_to_sheet([outHeaders, ...outRows]);
          }

          // In Sub-Department sheet, merge corresponding From/To pairs into final columns (keep all other columns)
          if (subDepartmentSheetName && workbook.Sheets[subDepartmentSheetName]) {
            const subDeptSheet = workbook.Sheets[subDepartmentSheetName];
            const aoa = XLSX.utils.sheet_to_json(subDeptSheet, { header: 1, defval: "" });
            const headers = (aoa?.[0] ?? []).map((h) => String(h ?? "").trim());

            const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedHeaders = headers.map(normalizeHeader);
            const headerIndex = new Map(normalizedHeaders.map((h, i) => [h, i]));

            const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const pickToFrom = (row, toIdx, fromIdx) => {
              const toVal = toIdx >= 0 ? row?.[toIdx] : "";
              const fromVal = fromIdx >= 0 ? row?.[fromIdx] : "";
              return hasValue(toVal) ? toVal : (fromVal ?? "");
            };

            const mergeSpecs = [
              { from: "From Branch", to: "To Branch", out: "Branch" },
              { from: "From Department", to: "To Department", out: "Department" },
              { from: "From Designation", to: "To Designation", out: "Designation" },
              { from: "From Grade", to: "To Grade", out: "Grade" },
              { from: "From Category", to: "To Category", out: "Category" },
              { from: "From Reporting Manager", to: "To Reporting Manager", out: "Reporting Manager" },
            ].map((s) => ({
              ...s,
              fromKey: normalizeHeader(s.from),
              toKey: normalizeHeader(s.to),
            }));

            const indicesByOut = new Map(
              mergeSpecs.map((s) => {
                const fromIdx = headerIndex.has(s.fromKey) ? headerIndex.get(s.fromKey) : -1;
                const toIdx = headerIndex.has(s.toKey) ? headerIndex.get(s.toKey) : -1;
                return [s.out, { fromIdx, toIdx }];
              }),
            );

            const excludedIdx = new Set();
            for (const { fromIdx, toIdx } of indicesByOut.values()) {
              if (fromIdx >= 0) excludedIdx.add(fromIdx);
              if (toIdx >= 0) excludedIdx.add(toIdx);
            }

            const firstPosByOut = new Map(
              mergeSpecs.map((s) => {
                const { fromIdx, toIdx } = indicesByOut.get(s.out) || { fromIdx: -1, toIdx: -1 };
                const pos =
                  fromIdx >= 0 && toIdx >= 0 ? Math.min(fromIdx, toIdx) : (fromIdx >= 0 ? fromIdx : toIdx);
                return [s.out, typeof pos === "number" ? pos : -1];
              }),
            );

            const outHeaders = [];
            const headerPlan = [];
            const insertedOut = new Set();

            for (let i = 0; i < headers.length; i++) {
              for (const spec of mergeSpecs) {
                const insertPos = firstPosByOut.get(spec.out);
                const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
                if (
                  insertPos === i &&
                  (fromIdx >= 0 || toIdx >= 0) &&
                  !insertedOut.has(spec.out)
                ) {
                  insertedOut.add(spec.out);
                  outHeaders.push(spec.out);
                  headerPlan.push({ kind: "merged", out: spec.out });
                }
              }

              if (!excludedIdx.has(i)) {
                outHeaders.push(headers[i]);
                headerPlan.push({ kind: "orig", idx: i });
              }
            }

            for (const spec of mergeSpecs) {
              const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
              if ((fromIdx >= 0 || toIdx >= 0) && !insertedOut.has(spec.out)) {
                insertedOut.add(spec.out);
                outHeaders.push(spec.out);
                headerPlan.push({ kind: "merged", out: spec.out });
              }
            }

            const outRows = aoa.slice(1).map((row) =>
              headerPlan.map((p) => {
                if (p.kind === "orig") return row?.[p.idx] ?? "";
                const { fromIdx, toIdx } = indicesByOut.get(p.out) || { fromIdx: -1, toIdx: -1 };
                return pickToFrom(row, toIdx, fromIdx);
              }),
            );

            workbook.Sheets[subDepartmentSheetName] = XLSX.utils.aoa_to_sheet([outHeaders, ...outRows]);
          }

          // In Designation sheet, merge corresponding From/To pairs into final columns (keep all other columns)
          if (designationSheetName && workbook.Sheets[designationSheetName]) {
            const desigSheet = workbook.Sheets[designationSheetName];
            const aoa = XLSX.utils.sheet_to_json(desigSheet, { header: 1, defval: "" });
            const headers = (aoa?.[0] ?? []).map((h) => String(h ?? "").trim());

            const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedHeaders = headers.map(normalizeHeader);
            const headerIndex = new Map(normalizedHeaders.map((h, i) => [h, i]));

            const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const pickToFrom = (row, toIdx, fromIdx) => {
              const toVal = toIdx >= 0 ? row?.[toIdx] : "";
              const fromVal = fromIdx >= 0 ? row?.[fromIdx] : "";
              return hasValue(toVal) ? toVal : (fromVal ?? "");
            };

            const mergeSpecs = [
              { from: "From Branch", to: "To Branch", out: "Branch" },
              { from: "From Department", to: "To Department", out: "Department" },
              { from: "From Sub-Department", to: "To Sub-Department", out: "Sub-Department" },
              { from: "From Grade", to: "To Grade", out: "Grade" },
              { from: "From Category", to: "To Category", out: "Category" },
              { from: "From Reporting Manager", to: "To Reporting Manager", out: "Reporting Manager" },
            ].map((s) => ({
              ...s,
              fromKey: normalizeHeader(s.from),
              toKey: normalizeHeader(s.to),
            }));

            const indicesByOut = new Map(
              mergeSpecs.map((s) => {
                const fromIdx = headerIndex.has(s.fromKey) ? headerIndex.get(s.fromKey) : -1;
                const toIdx = headerIndex.has(s.toKey) ? headerIndex.get(s.toKey) : -1;
                return [s.out, { fromIdx, toIdx }];
              }),
            );

            const excludedIdx = new Set();
            for (const { fromIdx, toIdx } of indicesByOut.values()) {
              if (fromIdx >= 0) excludedIdx.add(fromIdx);
              if (toIdx >= 0) excludedIdx.add(toIdx);
            }

            const firstPosByOut = new Map(
              mergeSpecs.map((s) => {
                const { fromIdx, toIdx } = indicesByOut.get(s.out) || { fromIdx: -1, toIdx: -1 };
                const pos =
                  fromIdx >= 0 && toIdx >= 0 ? Math.min(fromIdx, toIdx) : (fromIdx >= 0 ? fromIdx : toIdx);
                return [s.out, typeof pos === "number" ? pos : -1];
              }),
            );

            const outHeaders = [];
            const headerPlan = [];
            const insertedOut = new Set();

            for (let i = 0; i < headers.length; i++) {
              for (const spec of mergeSpecs) {
                const insertPos = firstPosByOut.get(spec.out);
                const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
                if (
                  insertPos === i &&
                  (fromIdx >= 0 || toIdx >= 0) &&
                  !insertedOut.has(spec.out)
                ) {
                  insertedOut.add(spec.out);
                  outHeaders.push(spec.out);
                  headerPlan.push({ kind: "merged", out: spec.out });
                }
              }

              if (!excludedIdx.has(i)) {
                outHeaders.push(headers[i]);
                headerPlan.push({ kind: "orig", idx: i });
              }
            }

            for (const spec of mergeSpecs) {
              const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
              if ((fromIdx >= 0 || toIdx >= 0) && !insertedOut.has(spec.out)) {
                insertedOut.add(spec.out);
                outHeaders.push(spec.out);
                headerPlan.push({ kind: "merged", out: spec.out });
              }
            }

            const outRows = aoa.slice(1).map((row) =>
              headerPlan.map((p) => {
                if (p.kind === "orig") return row?.[p.idx] ?? "";
                const { fromIdx, toIdx } = indicesByOut.get(p.out) || { fromIdx: -1, toIdx: -1 };
                return pickToFrom(row, toIdx, fromIdx);
              }),
            );

            workbook.Sheets[designationSheetName] = XLSX.utils.aoa_to_sheet([outHeaders, ...outRows]);
          }

          // In Grade sheet, merge corresponding From/To pairs into final columns (keep all other columns)
          if (gradeSheetName && workbook.Sheets[gradeSheetName]) {
            const gradeSheet = workbook.Sheets[gradeSheetName];
            const aoa = XLSX.utils.sheet_to_json(gradeSheet, { header: 1, defval: "" });
            const headers = (aoa?.[0] ?? []).map((h) => String(h ?? "").trim());

            const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedHeaders = headers.map(normalizeHeader);
            const headerIndex = new Map(normalizedHeaders.map((h, i) => [h, i]));

            const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const pickToFrom = (row, toIdx, fromIdx) => {
              const toVal = toIdx >= 0 ? row?.[toIdx] : "";
              const fromVal = fromIdx >= 0 ? row?.[fromIdx] : "";
              return hasValue(toVal) ? toVal : (fromVal ?? "");
            };

            const mergeSpecs = [
              { from: "From Branch", to: "To Branch", out: "Branch" },
              { from: "From Department", to: "To Department", out: "Department" },
              { from: "From Sub-Department", to: "To Sub-Department", out: "Sub-Department" },
              { from: "From Designation", to: "To Designation", out: "Designation" },
              { from: "From Category", to: "To Category", out: "Category" },
              { from: "From Reporting Manager", to: "To Reporting Manager", out: "Reporting Manager" },
            ].map((s) => ({
              ...s,
              fromKey: normalizeHeader(s.from),
              toKey: normalizeHeader(s.to),
            }));

            const indicesByOut = new Map(
              mergeSpecs.map((s) => {
                const fromIdx = headerIndex.has(s.fromKey) ? headerIndex.get(s.fromKey) : -1;
                const toIdx = headerIndex.has(s.toKey) ? headerIndex.get(s.toKey) : -1;
                return [s.out, { fromIdx, toIdx }];
              }),
            );

            const excludedIdx = new Set();
            for (const { fromIdx, toIdx } of indicesByOut.values()) {
              if (fromIdx >= 0) excludedIdx.add(fromIdx);
              if (toIdx >= 0) excludedIdx.add(toIdx);
            }

            const firstPosByOut = new Map(
              mergeSpecs.map((s) => {
                const { fromIdx, toIdx } = indicesByOut.get(s.out) || { fromIdx: -1, toIdx: -1 };
                const pos =
                  fromIdx >= 0 && toIdx >= 0 ? Math.min(fromIdx, toIdx) : (fromIdx >= 0 ? fromIdx : toIdx);
                return [s.out, typeof pos === "number" ? pos : -1];
              }),
            );

            const outHeaders = [];
            const headerPlan = [];
            const insertedOut = new Set();

            for (let i = 0; i < headers.length; i++) {
              for (const spec of mergeSpecs) {
                const insertPos = firstPosByOut.get(spec.out);
                const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
                if (
                  insertPos === i &&
                  (fromIdx >= 0 || toIdx >= 0) &&
                  !insertedOut.has(spec.out)
                ) {
                  insertedOut.add(spec.out);
                  outHeaders.push(spec.out);
                  headerPlan.push({ kind: "merged", out: spec.out });
                }
              }

              if (!excludedIdx.has(i)) {
                outHeaders.push(headers[i]);
                headerPlan.push({ kind: "orig", idx: i });
              }
            }

            for (const spec of mergeSpecs) {
              const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
              if ((fromIdx >= 0 || toIdx >= 0) && !insertedOut.has(spec.out)) {
                insertedOut.add(spec.out);
                outHeaders.push(spec.out);
                headerPlan.push({ kind: "merged", out: spec.out });
              }
            }

            const outRows = aoa.slice(1).map((row) =>
              headerPlan.map((p) => {
                if (p.kind === "orig") return row?.[p.idx] ?? "";
                const { fromIdx, toIdx } = indicesByOut.get(p.out) || { fromIdx: -1, toIdx: -1 };
                return pickToFrom(row, toIdx, fromIdx);
              }),
            );

            workbook.Sheets[gradeSheetName] = XLSX.utils.aoa_to_sheet([outHeaders, ...outRows]);
          }

          // In Category sheet, merge corresponding From/To pairs into final columns (keep all other columns)
          if (categorySheetName && workbook.Sheets[categorySheetName]) {
            const categorySheet = workbook.Sheets[categorySheetName];
            const aoa = XLSX.utils.sheet_to_json(categorySheet, { header: 1, defval: "" });
            const headers = (aoa?.[0] ?? []).map((h) => String(h ?? "").trim());

            const normalizeHeader = (h) => String(h ?? "").toLowerCase().replace(/\s+/g, " ").trim();
            const normalizedHeaders = headers.map(normalizeHeader);
            const headerIndex = new Map(normalizedHeaders.map((h, i) => [h, i]));

            const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
            const pickToFrom = (row, toIdx, fromIdx) => {
              const toVal = toIdx >= 0 ? row?.[toIdx] : "";
              const fromVal = fromIdx >= 0 ? row?.[fromIdx] : "";
              return hasValue(toVal) ? toVal : (fromVal ?? "");
            };

            const mergeSpecs = [
              { from: "From Branch", to: "To Branch", out: "Branch" },
              { from: "From Department", to: "To Department", out: "Department" },
              { from: "From Sub-Department", to: "To Sub-Department", out: "Sub-Department" },
              { from: "From Designation", to: "To Designation", out: "Designation" },
              { from: "From Grade", to: "To Grade", out: "Grade" },
              { from: "From Reporting Manager", to: "To Reporting Manager", out: "Reporting Manager" },
            ].map((s) => ({
              ...s,
              fromKey: normalizeHeader(s.from),
              toKey: normalizeHeader(s.to),
            }));

            const indicesByOut = new Map(
              mergeSpecs.map((s) => {
                const fromIdx = headerIndex.has(s.fromKey) ? headerIndex.get(s.fromKey) : -1;
                const toIdx = headerIndex.has(s.toKey) ? headerIndex.get(s.toKey) : -1;
                return [s.out, { fromIdx, toIdx }];
              }),
            );

            const excludedIdx = new Set();
            for (const { fromIdx, toIdx } of indicesByOut.values()) {
              if (fromIdx >= 0) excludedIdx.add(fromIdx);
              if (toIdx >= 0) excludedIdx.add(toIdx);
            }

            const firstPosByOut = new Map(
              mergeSpecs.map((s) => {
                const { fromIdx, toIdx } = indicesByOut.get(s.out) || { fromIdx: -1, toIdx: -1 };
                const pos =
                  fromIdx >= 0 && toIdx >= 0 ? Math.min(fromIdx, toIdx) : (fromIdx >= 0 ? fromIdx : toIdx);
                return [s.out, typeof pos === "number" ? pos : -1];
              }),
            );

            const outHeaders = [];
            const headerPlan = [];
            const insertedOut = new Set();

            for (let i = 0; i < headers.length; i++) {
              for (const spec of mergeSpecs) {
                const insertPos = firstPosByOut.get(spec.out);
                const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
                if (
                  insertPos === i &&
                  (fromIdx >= 0 || toIdx >= 0) &&
                  !insertedOut.has(spec.out)
                ) {
                  insertedOut.add(spec.out);
                  outHeaders.push(spec.out);
                  headerPlan.push({ kind: "merged", out: spec.out });
                }
              }

              if (!excludedIdx.has(i)) {
                outHeaders.push(headers[i]);
                headerPlan.push({ kind: "orig", idx: i });
              }
            }

            for (const spec of mergeSpecs) {
              const { fromIdx, toIdx } = indicesByOut.get(spec.out) || { fromIdx: -1, toIdx: -1 };
              if ((fromIdx >= 0 || toIdx >= 0) && !insertedOut.has(spec.out)) {
                insertedOut.add(spec.out);
                outHeaders.push(spec.out);
                headerPlan.push({ kind: "merged", out: spec.out });
              }
            }

            const outRows = aoa.slice(1).map((row) =>
              headerPlan.map((p) => {
                if (p.kind === "orig") return row?.[p.idx] ?? "";
                const { fromIdx, toIdx } = indicesByOut.get(p.out) || { fromIdx: -1, toIdx: -1 };
                return pickToFrom(row, toIdx, fromIdx);
              }),
            );

            workbook.Sheets[categorySheetName] = XLSX.utils.aoa_to_sheet([outHeaders, ...outRows]);
          }

          removeJunkSheetsFromWorkbook(workbook);

          // Rebuild a fresh workbook from cell values only. This avoids corrupt OOXML that Excel
          // Online flags as "damaged" / repair (Recovered_Sheet1) when round-tripping styled or
          // partially mutated workbooks through SheetJS.
          const rebuildCleanWorkbookForExcel = (wb) => {
            const out = XLSX.utils.book_new();
            const safeSheetName = (name, used) => {
              let s = String(name ?? "Sheet")
                .replace(/[:\\/?*[\]]/g, "_")
                .trim()
                .slice(0, 31);
              if (!s) s = "Sheet";
              let unique = s;
              let i = 1;
              while (used.has(unique)) {
                const suffix = `_${i++}`;
                unique = `${s.slice(0, Math.max(0, 31 - suffix.length))}${suffix}`.slice(0, 31);
              }
              used.add(unique);
              return unique;
            };
            const used = new Set();
            for (const rawName of wb.SheetNames || []) {
              if (!rawName || !wb.Sheets[rawName]) continue;
              const ws = wb.Sheets[rawName];
              let aoa;
              try {
                aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
              } catch {
                continue;
              }
              if (!aoa || !aoa.length) aoa = [[""]];
              const cleanWs = XLSX.utils.aoa_to_sheet(aoa);
              XLSX.utils.book_append_sheet(out, cleanWs, safeSheetName(rawName, used));
            }
            return out;
          };

          let finalWorkbook = rebuildCleanWorkbookForExcel(workbook);
          if (!finalWorkbook.SheetNames?.length) {
            finalWorkbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
              finalWorkbook,
              XLSX.utils.aoa_to_sheet([["No data found"]]),
              "Transfers"
            );
          }

          const updatedArray = XLSX.write(finalWorkbook, {
            bookType: "xlsx",
            type: "array",
            bookSST: false,
          });
          const updatedBlob = new Blob([updatedArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = window.URL.createObjectURL(updatedBlob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const monthYearLabel = formatMonthYearForReports(yearNum, monthNum);
          a.download = buildReportFilename(companyCode, "Transfers Report", monthYearLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Transfers report exported.");
          return;
        }
        if (activeSubTab === "resignations" && resignationsReportMonth) {
          const [y, m] = resignationsReportMonth.split("-");
          const monthNum = parseInt(m, 10);
          const yearNum = parseInt(y, 10);
          const response = await employeeService.exportResignationsReport(monthNum, yearNum);
          if (!response?.data) {
            toast.error("Export failed. No file received.");
            return;
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const monthYearLabel = formatMonthYearForReports(yearNum, monthNum);
          a.download = buildReportFilename(companyCode, "Resignations Report", monthYearLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Resignations report exported.");
          return;
        }
        if (activeSubTab === "service-report") {
          const params = {
            dateTo: currentFilters.reportDateTo || undefined,
            dateFrom: currentFilters.reportDateFrom || undefined,
            branch: currentFilters.branch || undefined,
            department: currentFilters.department || undefined,
            designation: currentFilters.designation || undefined,
            grade: currentFilters.grade || undefined,
            category: currentFilters.category || undefined,
            subDepartment: currentFilters.subDepartment || undefined,
          };
          const response = await employeeService.exportServiceReport(params);
          if (!response?.data) {
            toast.error("Export failed. No file received.");
            return;
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const rangeLabel =
            params.dateFrom && params.dateTo
              ? `${params.dateFrom} to ${params.dateTo}`
              : getTodayDdMmYyyy();
          a.download = buildReportFilename(companyCode, "Employee Service Report", rangeLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Employee Service report exported.");
          return;
        }
        if (activeSubTab === "age-report") {
          const params = {
            branch: currentFilters.branch || undefined,
            department: currentFilters.department || undefined,
            designation: currentFilters.designation || undefined,
            category: currentFilters.category || undefined,
          };
          const response = await employeeService.exportAgeReport(params);
          if (!response?.data) {
            toast.error("Export failed. No file received.");
            return;
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const companyCode = getCompanyCodeForReports();
          const todayLabel = getTodayDdMmYyyy();
          a.download = buildReportFilename(companyCode, "Employee Age Report", todayLabel);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success("Employee Age report exported.");
          return;
        }

        let exportDataList;
        if (activeSubTab === "salary-revision") {
          exportDataList = salaryRevisionReportData;
        } else {
          exportDataList = loadedEmployees;
          if (!exportDataList || exportDataList.length === 0) {
            exportDataList = await fetchReportData(1, true);
          }
        }

        if (!exportDataList || exportDataList.length === 0) {
          toast.error("No data to export");
          return;
        }

        // Helper function to get name by ID from master data
        const getNameById = (id, masterDataArray) => {
          if (!id || !masterDataArray) return "";
          const item = masterDataArray.find(
            (item) => item.id === id || item.id === parseInt(id)
          );
          return item ? item.name : "";
        };

        // Helper function to get employee name by ID
        const getEmployeeNameById = (id) => {
          if (!id) return "";
          // Note: This might not work if we don't have all employees loaded.
          // Ideally, the backend should return the manager name.
          // For now, we'll try to find it in the exported list itself or skip it.
          const emp = exportDataList.find(
            (emp) => emp.id === id || emp.id === parseInt(id)
          );
          return emp ? emp.user?.name || emp.name || "" : "";
        };

        // Helper function to format dates
        const formatDateForExport = (date) => {
          if (!date) return "";
          try {
            return new Date(date).toLocaleDateString("en-GB"); // dd/mm/yyyy format
          } catch (error) {
            return "";
          }
        };

        // Helper function to get resignation reason text
        const getResignationReasonText = (reasonCode) => {
          if (!reasonCode) return "";
          // Map reason codes to text (you can expand this based on your data)
          const reasonMap = {
            1: "Health issues",
            2: "Better opportunity",
            3: "Personal reasons",
            4: "Career change",
            5: "Family reasons",
            6: "Relocation",
            7: "Retirement",
            8: "Termination",
            9: "Contract end",
            10: "Other",
          };
          return reasonMap[reasonCode] || reasonCode;
        };

        let exportData;

        // Salary Revision Report: only these columns
        if (activeSubTab === "salary-revision") {
          exportData = exportDataList.map((item, index) => {
            const revision = item;
            const employee = revision.employee;
            const prevCtc = revision.previousCtc ?? 0;
            const newCtc = revision.ctcAmount ?? revision.newCtc ?? 0;
            const incrementAmt = (prevCtc != null && newCtc != null) ? (newCtc - prevCtc) : null;
            const incrementPct = (prevCtc != null && prevCtc > 0 && incrementAmt != null)
              ? calculatePercentageChange(prevCtc, newCtc)
              : null;
            const prevDesig = designations.find((d) => d.id === employee?.designationId)?.name ?? "";
            const newDesig = revision.designation ?? "";
            return {
              "Sl No": index + 1,
              "Emp Code": employee?.empCode || "",
              "Emp Name": employee?.user?.name || employee?.name || "",
              "Action Date": formatDateForExport(
                revision.actionDate || revision.createdAt || revision.updatedAt
              ),
              "Increment Effective Date": formatDateForExport(revision.effectiveDate),
              "Previous CTC": prevCtc != null && prevCtc !== "" ? (typeof prevCtc === "number" ? prevCtc : prevCtc) : "",
              "New CTC": newCtc != null && newCtc !== "" ? (typeof newCtc === "number" ? newCtc : newCtc) : "",
              "Increment Amount": incrementAmt != null ? incrementAmt : "",
              "Increment %": incrementPct != null ? incrementPct : "",
              "Previous Designation": prevDesig || "",
              "New Designation": newDesig || "",
              "Designation Changed": newDesig && prevDesig !== newDesig ? "Yes" : "No",
              "Reason": revision.reason || "",
              "Remarks": revision.remarks || "",
              "Last Activity Date": formatDateForExport(
                revision.createdAt || revision.updatedAt || revision.actionDate || revision.effectiveDate
              ),
            };
          });
        } else if (activeSubTab === "employees") {
          exportData = exportDataList.map((emp, index) => ({
            // Serial number as first column
            "Sl No": index + 1,
            // Basic Employee Information (10 fields)
            "Employee Code": emp.empCode || "",
            Name: emp.user?.name || emp.name || "",
            Gender: emp.user?.gender || "",
            "Date of Birth": formatDateForExport(emp.user?.dateOfBirth),
            "Mobile Number": emp.user?.phone || "",
            "Personal Email": emp.user?.email || "",
            "Official Email": emp.email || "",
            "Blood Group": emp.user?.bloodGroup || "",
            "Aadhaar Number": emp.aadharNumber || emp.user?.aadhaarNumber || "",
            "PAN Number": emp.panNumber || emp.user?.panNumber || "",

            // Organization Structure (7 fields) - Get names from master data
            Branch:
              getNameById(emp.branchId, branches) || emp.branch?.name || "",
            Designation:
              getNameById(emp.designationId, designations) ||
              emp.designation?.name ||
              "",
            Department:
              getNameById(emp.departmentId, departments) ||
              emp.department?.name ||
              "",
            "Sub Department":
              getNameById(emp.subDepartmentId, subDepartments) ||
              emp.subDepartment?.name ||
              "",
            Grade: getNameById(emp.gradeId, grades) || emp.grade?.name || "",
            Category:
              getNameById(emp.categoryId, categories) ||
              emp.category?.name ||
              "",
            "Reporting Manager":
              getEmployeeNameById(emp.reportingManagerId) ||
              emp.reportingManager?.name ||
              "",

            // Employment Details (4 fields)
            "Date of Join": formatDateForExport(
              emp.joiningDate || emp.dateOfJoin
            ),
            "Employment Type": emp.employmentType || "",
            "Employment Status": emp.employmentStatus || "",
            "CTC (Cost to Company)": emp.ctc || "",

            // Conditional Fields (6 fields)
            "Confirmation Date": formatDateForExport(emp.confirmationDate),
            "Date of Resignation": formatDateForExport(emp.resignationDate),
            "Date of Relieved": formatDateForExport(emp.relievedDate),
            "Resignation/Termination Reason": getResignationReasonText(
              emp.resignationReason || emp.reason
            ),

            // Statutory Compliance (6 fields)
            "PF Applicable": emp.pfApplicable ? "Yes" : "No",
            "Ceiling PF": emp.ceilingPf ? "Yes" : "No",
            "EPS Applicable": emp.epsApplicable ? "Yes" : "No",
            "ESI Applicable": emp.esiApplicable ? "Yes" : "No",
            "PT Applicable": emp.ptApplicable ? "Yes" : "No",
            "Gratuity Applicable": emp.gratuityApplicable ? "Yes" : "No",

            // Additional Options (1 field)
            "Send Invite": emp.inviteSent ? "Yes" : "No",
          }));
        } else if (activeSubTab === "service-report") {
          exportData = exportDataList.map((employee, index) => {
            const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
            const service = getServiceYearsMonths(doj);
            const reportingManager = exportDataList.find(
              (e) => e.id === employee.reportingManagerId || e.id === parseInt(employee.reportingManagerId, 10)
            );
            return {
              "Sl No": index + 1,
              "Emp Code": employee.empCode || "",
              "Emp Name": employee.user?.name || employee.name || "",
              Branch: branches.find((b) => b.id === employee.branchId)?.name || "",
              Department: departments.find((d) => d.id === employee.departmentId)?.name || "",
              Designation: designations.find((d) => d.id === employee.designationId)?.name || "",
              "Sub Department": subDepartments.find((d) => d.id === employee.subDepartmentId)?.name || "",
              Grade: grades.find((g) => g.id === employee.gradeId)?.name || "",
              Category: categories.find((c) => c.id === employee.categoryId)?.name || "",
              "Employment Type": employee.employmentType || "",
              "Employment Status": employee.employmentStatus || "Active",
              "Date of Joining (DOJ)": doj ? formatDateForExport(doj) : "",
              "Confirmation Date": employee.confirmationDate ? formatDateForExport(employee.confirmationDate) : "",
              "Service(Years)": (service.years === "-" || service.months === "-") ? "-" : `${service.years}.${service.months}`,
              "Reporting Manager": reportingManager?.user?.name || reportingManager?.name || "",
            };
          });
        } else if (activeSubTab === "salary-report") {
          exportData = exportDataList.map((employee, index) => {
            const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
            return {
              "Sl No": index + 1,
              "Emp Code": employee.empCode || "",
              "Emp Name": employee.user?.name || employee.name || "",
              Branch: branches.find((b) => b.id === employee.branchId)?.name || "",
              Department: departments.find((d) => d.id === employee.departmentId)?.name || "",
              Designation: designations.find((d) => d.id === employee.designationId)?.name || "",
              Grade: grades.find((g) => g.id === employee.gradeId)?.name || "",
              Category: categories.find((c) => c.id === employee.categoryId)?.name || "",
              "Employment Type": employee.employmentType || "",
              "Date of Joining (DOJ)": doj ? formatDateForExport(doj) : "",
              CTC: employee.ctc != null && employee.ctc !== "" ? (typeof employee.ctc === "number" ? employee.ctc : employee.ctc) : "",
              "PF Applicable": employee.pfApplicable ? "Yes" : "No",
              "ESI Applicable": employee.esiApplicable ? "Yes" : "No",
              "Bank Name": employee.bankName || "",
              "Account Number": employee.accountNumber || "",
            };
          });
        } else if (activeSubTab === "confirmation") {
          exportData = exportDataList.map((employee, index) => {
            const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
            const probationExtended = employee.extendProbation ?? employee.probationExtended ?? false;
            const extendedDate = employee.extendedProbationDate;
            const remarks = employee.remarks ?? employee.confirmationRemarks ?? "";
            return {
              "Sl No": index + 1,
              "Emp Code": employee.empCode || "",
              "Emp Name": employee.user?.name || employee.name || "",
              Branch: branches.find((b) => b.id === employee.branchId)?.name || "",
              Department: departments.find((d) => d.id === employee.departmentId)?.name || "",
              Designation: designations.find((d) => d.id === employee.designationId)?.name || "",
              "Date of Joining (DOJ)": doj ? formatDateForExport(doj) : "",
              "Confirmation Date": employee.confirmationDate
                ? formatDateForExport(employee.confirmationDate)
                : "",
              "Probation Extended": probationExtended ? "Yes" : "No",
              "Extended Probation Date": extendedDate
                ? formatDateForExport(extendedDate)
                : "",
              Remarks: remarks,
            };
          });
        } else if (activeSubTab === "transfers") {
          exportData = exportDataList.map((item, index) => {
            const emp = item.employee || item;
            const effDate = item.effectiveDate ?? item.transferDate ?? item.transferEffectiveDate ?? emp.transferDate ?? emp.transferEffectiveDate;
            const fromBranch = branches.find((b) => b.id === (item.fromBranchId ?? emp.fromBranchId))?.name ?? item.fromBranch?.name ?? (typeof item.fromBranch === "string" ? item.fromBranch : "") ?? "";
            const toBranch = branches.find((b) => b.id === (item.toBranchId ?? emp.toBranchId))?.name ?? item.toBranch?.name ?? (typeof item.toBranch === "string" ? item.toBranch : "") ?? "";
            const fromDept = departments.find((d) => d.id === (item.fromDepartmentId ?? emp.fromDepartmentId))?.name ?? item.fromDepartment?.name ?? (typeof item.fromDepartment === "string" ? item.fromDepartment : "") ?? "";
            const toDept = departments.find((d) => d.id === (item.toDepartmentId ?? emp.toDepartmentId))?.name ?? item.toDepartment?.name ?? (typeof item.toDepartment === "string" ? item.toDepartment : "") ?? "";
            const fromDesig = designations.find((d) => d.id === (item.fromDesignationId ?? emp.fromDesignationId))?.name ?? item.fromDesignation?.name ?? (typeof item.fromDesignation === "string" ? item.fromDesignation : "") ?? "";
            const toDesig = designations.find((d) => d.id === (item.toDesignationId ?? emp.toDesignationId))?.name ?? item.toDesignation?.name ?? (typeof item.toDesignation === "string" ? item.toDesignation : "") ?? "";
            const fromGrade = grades.find((g) => g.id === (item.fromGradeId ?? emp.fromGradeId))?.name ?? item.fromGrade?.name ?? (typeof item.fromGrade === "string" ? item.fromGrade : "") ?? "";
            const toGrade = grades.find((g) => g.id === (item.toGradeId ?? emp.toGradeId))?.name ?? item.toGrade?.name ?? (typeof item.toGrade === "string" ? item.toGrade : "") ?? "";
            const fromCat = categories.find((c) => c.id === (item.fromCategoryId ?? emp.fromCategoryId))?.name ?? item.fromCategory?.name ?? (typeof item.fromCategory === "string" ? item.fromCategory : "") ?? "";
            const toCat = categories.find((c) => c.id === (item.toCategoryId ?? emp.toCategoryId))?.name ?? item.toCategory?.name ?? (typeof item.toCategory === "string" ? item.toCategory : "") ?? "";
            const reason = item.reason ?? item.transferReason ?? emp.transferReason ?? emp.reason ?? "";
            const remarksVal = item.remarks ?? item.transferRemarks ?? emp.transferRemarks ?? emp.remarks ?? "";
            return {
              "Sl No": index + 1,
              "Emp Code": emp.empCode || "",
              "Emp Name": emp.user?.name || emp.name || "",
              "Effective Date": effDate ? formatDateForExport(effDate) : "",
              "From Branch": fromBranch,
              "To Branch": toBranch,
              "From Department": fromDept,
              "To Department": toDept,
              "From Designation": fromDesig,
              "To Designation": toDesig,
              "From Grade": fromGrade,
              "To Grade": toGrade,
              "From Category": fromCat,
              "To Category": toCat,
              "Reason": reason,
              "Remarks": remarksVal,
            };
          });
        } else if (activeSubTab === "resignations") {
          exportData = exportDataList.map((employee, index) => {
            const doj = employee.dateOfJoin ?? employee.joiningDate ?? employee.joinDate;
            const resignationDate = employee.resignationDate;
            const relievingDate = employee.relievingDate ?? employee.dateOfRelieving ?? employee.dateOfLeaving ?? employee.relievedDate;
            const noticePeriod = employee.noticePeriodDays ?? employee.noticePeriod ?? "";
            const noticeWaived = employee.noticePeriodWaiverDays ?? employee.noticeWaived ?? "";
            const noticeToDeduct = employee.noticePeriodToDeduct ?? employee.noticeToDeduct ?? "";
            const reason = employee.resignationReason ?? employee.reason ?? "";
            const retro = employee.retroResignation ?? employee.isRetroResignation ?? false;
            const remarksVal = employee.remarks ?? employee.resignationRemarks ?? "";
            return {
              "Sl No": index + 1,
              "Emp Code": employee.empCode || "",
              "Emp Name": employee.user?.name || employee.name || "",
              "Branch": branches.find((b) => b.id === employee.branchId)?.name || "",
              "Department": departments.find((d) => d.id === employee.departmentId)?.name || "",
              "Designation": designations.find((d) => d.id === employee.designationId)?.name || "",
              "Date of Joining (DOJ)": doj ? formatDateForExport(doj) : "",
              "Resignation Date": resignationDate ? formatDateForExport(resignationDate) : "",
              "Relieving Date": relievingDate ? formatDateForExport(relievingDate) : "",
              "Notice Period (Days)": noticePeriod,
              "Notice Waived (Days)": noticeWaived,
              "Notice to Deduct": noticeToDeduct,
              "Reason": reason,
              "Retro Resignation": retro ? "Yes" : "No",
              "Remarks": remarksVal,
            };
          });
        } else {
          // For other reports, use basic export data
          exportData = exportDataList.map((employee, index) => {
            const employeeDate = getEmployeeDateForReport(
              employee,
              activeSubTab
            );
            return {
              "Sl No": index + 1,
              "Employee Code": employee.empCode || "",
              Name: employee.user?.name || employee.name || "",
              Department:
                departments.find((d) => d.id === employee.departmentId)?.name ||
                "",
              Designation:
                designations.find((d) => d.id === employee.designationId)
                  ?.name || "",
              [getReportDateLabel(activeSubTab)]: employeeDate
                ? formatDateForExport(employeeDate)
                : "",
              Status: employee.employmentStatus || "Active",
              Branch:
                branches.find((b) => b.id === employee.branchId)?.name || "",
              Grade: grades.find((g) => g.id === employee.gradeId)?.name || "",
              Category:
                categories.find((c) => c.id === employee.categoryId)?.name ||
                "",
              "Employment Type": employee.employmentType || "",
              Email: employee.user?.email || employee.email || "",
              Phone: employee.user?.phone || employee.phone || "",
              "Date of Birth": formatDateForExport(employee.user?.dateOfBirth),
              Gender: employee.user?.gender || employee.gender || "",
              Address: employee.user?.address || employee.address || "",
              City: employee.user?.city || employee.city || "",
              State: employee.user?.state || employee.state || "",
              Pincode: employee.user?.pincode || employee.pincode || "",
              "Aadhaar Number":
                employee.user?.aadhaarNumber || employee.aadhaarNumber || "",
              "PAN Number":
                employee.user?.panNumber || employee.panNumber || "",
              "Blood Group":
                employee.user?.bloodGroup || employee.bloodGroup || "",
              "Emergency Contact":
                employee.user?.emergencyContact ||
                employee.emergencyContact ||
                "",
              "Emergency Phone":
                employee.user?.emergencyPhone || employee.emergencyPhone || "",
            };
          });
        }

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Set column widths to auto-fit based on content
        const colWidths = Object.keys(exportData[0] || {}).map((key, index) => {
          const maxLength = Math.max(
            key.length, // Header length
            ...exportData.map((row) => String(row[key] || "").length) // Content length
          );
          return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }; // Min 10, Max 50
        });
        worksheet["!cols"] = colWidths;

        // Define columns that should be center aligned (for New Joinee Report)
        const centerAlignedColumns = [
          "Sl No",
          "Employee Code",
          "Date of Birth",
          "Date of Join",
          "Confirmation Date",
          "Date of Resignation",
          "Date of Relieved",
          "Blood Group",
          "Aadhaar Number",
          "PAN Number",
          "Grade",
          "PF Applicable",
          "Ceiling PF",
          "EPS Applicable",
          "ESI Applicable",
          "PT Applicable",
          "Gratuity Applicable",
          "Send Invite",
        ];

        // Get column indices for center alignment
        const columnKeys = Object.keys(exportData[0] || {});
        const centerAlignedIndices = centerAlignedColumns.map((col) =>
          columnKeys.indexOf(col)
        );

        // Style all cells with font and vertical alignment
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let row = range.s.r; row <= range.e.r; row++) {
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) continue;

            const isHeader = row === 0;
            const isCenterAligned = centerAlignedIndices.includes(col);

            // Set cell style
            worksheet[cellAddress].s = {
              font: {
                name: "Segoe UI",
                sz: 10,
                bold: isHeader,
                color: { rgb: "000000" },
              },
              fill: isHeader
                ? {
                    patternType: "solid",
                    fgColor: { rgb: "F0F0F0" },
                  }
                : undefined,
              alignment: {
                horizontal: isCenterAligned || isHeader ? "center" : "left",
                vertical: "center",
                wrapText: true,
              },
            };
          }
        }

        // Set freeze panes (freeze from column D and row 2) for Employees Report
        if (activeSubTab === "employees") {
          if (!worksheet["!views"]) worksheet["!views"] = [{}];
          worksheet["!views"][0] = {
            state: "frozen",
            xSplit: 3, // Freeze at column D (index 3)
            ySplit: 1, // Freeze at row 2 (index 1)
          };
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, reportType);

        // Generate filename: ReportName_Month_YYYY.xlsx (e.g. Salary_Revision_Report_February_2026.xlsx)
        const now = new Date();
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];
        const monthName = monthNames[now.getMonth()];
        const yyyy = now.getFullYear();
        const dateStr = `${monthName}_${yyyy}`;

        const exportFileNames = {
          employees: "Employees_Report",
          "age-report": "Employee_Age_Report",
          "service-report": "Employee_Service_Report",
          "salary-report": "Salary_Report",
          confirmation: "Confirmation_Report",
          transfers: "Transfers_Report",
          "salary-revision": "Salary_Revision_Report",
          resignations: "Resignations_Report",
        };
        const baseName = exportFileNames[activeSubTab] || reportType.replace(/\s+/g, "_");
        const filename = `${baseName}_${dateStr}.xlsx`;

        // Write and download
        XLSX.writeFile(workbook, filename);

        toast.success(`${reportType} downloaded successfully.`);
      } catch (error) {
        console.error("Export error:", error);
        toast.error("Failed to download report. Please try again.");
      }
    };


    return (
      <div className="flex-1 overflow-auto">
        <style>{`
          @keyframes blink-border {
            0%, 100% { border-color: rgb(59 130 246); }
            50% { border-color: rgb(37 99 235); }
          }
        `}</style>
        {showFilterPopup && (
          <ReportFiltersPopup
            darkMode={darkMode}
            filters={currentFilters}
            onFilterChange={handleFilterChange}
            onClose={() => setShowFilterPopup(false)}
            onClear={clearFilters}
            onApply={async () => {
              const params = buildReportExportParams();
              const isEmployeesOrAgeOrService = ["employees", "age-report", "service-report"].includes(activeSubTab);
              if (activeSubTab === "employees") setEmployeesReportSummaryLoading(true);
              if (activeSubTab === "age-report") setAgeReportSummaryLoading(true);
              if (activeSubTab === "service-report") setServiceReportSummaryLoading(true);
              try {
                if (isEmployeesOrAgeOrService) {
                  const res = await employeeService.getEmployeesReportSummary(params);
                  const count = res?.data?.count ?? 0;
                  if (activeSubTab === "employees") setEmployeesReportCount(count);
                  if (activeSubTab === "age-report") setAgeReportCount(count);
                  if (activeSubTab === "service-report") setServiceReportCount(count);
                  setShowEmployeeList(true);
                  setShowFilterPopup(false);
                  toast.success(`Filters applied. ${count} employee${count !== 1 ? "s" : ""} match your criteria.`);
                } else {
                  setShowFilterPopup(false);
                  toast.success("Filters applied. Select month and load summary, or click Download to export with these filters.");
                }
              } catch (err) {
                console.error("Report summary error:", err);
                toast.error(err?.response?.data?.message || "Failed to load count.");
              } finally {
                if (activeSubTab === "employees") setEmployeesReportSummaryLoading(false);
                if (activeSubTab === "age-report") setAgeReportSummaryLoading(false);
                if (activeSubTab === "service-report") setServiceReportSummaryLoading(false);
              }
            }}
            applyLoading={["employees", "age-report", "service-report"].includes(activeSubTab) && (employeesReportSummaryLoading || ageReportSummaryLoading || serviceReportSummaryLoading)}
            branches={branches}
            departments={departments}
            designations={designations}
            grades={grades}
            categories={categories}
            subDepartments={subDepartments}
            reportingManagers={reportingManagers}
          />
        )}
        <div className="max-h-full bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]">
          <div className="flex h-full">
            {/* Vertical Sidebar */}
            {!hideSidebar && (
              <div
                className={`w-80 border-r ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#3C3C3C]"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
              <div className="p-6">
                <h1
                  className={`text-2xl font-bold mb-8 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                  }`}
                >
                  Reports
                </h1>

                {/* Main Categories (hidden when embedded in Employee module) */}
                {!isEmployeeOnly && (
                  <div className="space-y-2 mb-8">
                    <button
                      onClick={() => setActiveTab("employees")}
                      className={`w-full flex items-center px-4 py-3 rounded-md text-left transition-colors ${
                        activeTab === "employees"
                          ? `${
                              darkMode
                                ? "bg-redhalo-navy text-white"
                                : "rh-btn-primary"
                            }`
                          : `${
                              darkMode
                                ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                : "text-gray-700 hover:bg-gray-100"
                            }`
                      }`}
                    >
                      <Users size={20} className="mr-3" />
                      <span className="font-medium">Employee Reports</span>
                    </button>

                    <button
                      onClick={() => setActiveTab("attendance")}
                      className={`w-full flex items-center px-4 py-3 rounded-md text-left transition-colors ${
                        activeTab === "attendance"
                          ? `${
                              darkMode
                                ? "bg-redhalo-navy text-white"
                                : "rh-btn-primary"
                            }`
                          : `${
                              darkMode
                                ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                : "text-gray-700 hover:bg-gray-100"
                            }`
                      }`}
                    >
                      <Clock size={20} className="mr-3" />
                      <span className="font-medium">Attendance Reports</span>
                    </button>

                    <button
                      onClick={() => setActiveTab("payroll")}
                      className={`w-full flex items-center px-4 py-3 rounded-md text-left transition-colors ${
                        activeTab === "payroll"
                          ? `${
                              darkMode
                                ? "bg-redhalo-navy text-white"
                                : "rh-btn-primary"
                            }`
                          : `${
                              darkMode
                                ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                : "text-gray-700 hover:bg-gray-100"
                            }`
                      }`}
                    >
                      <DollarSign size={20} className="mr-3" />
                      <span className="font-medium">Payroll Reports</span>
                    </button>
                  </div>
                )}

                {/* Employee report list (always shown in employee-only mode) */}
                {(activeTab === "employees" || isEmployeeOnly) && (
                  <div className="space-y-1">
                    <h3
                      className={`text-sm font-semibold mb-3 ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                      }`}
                    >
                      EMPLOYEE REPORTS
                    </h3>
                    {[
                      {
                        id: "employees",
                        label: "Employees Report",
                        icon: Users,
                      },
                      {
                        id: "age-report",
                        label: "Employee Age Report",
                        icon: Calendar,
                      },
                      {
                        id: "service-report",
                        label: "Employee Service Report",
                        icon: SafeTrendingUp,
                      },
                      {
                        id: "salary-report",
                        label: "Salary Report",
                        icon: DollarIcon,
                      },
                      {
                        id: "confirmation",
                        label: "Confirmation Report",
                        icon: UserCheck,
                      },
                      {
                        id: "transfers",
                        label: "Transfers Report",
                        icon: ArrowRightLeft,
                      },
                      {
                        id: "salary-revision",
                        label: "Salary Revision Report",
                        icon: SafeTrendingUp,
                      },
                      {
                        id: "resignations",
                        label: "Resignations Report",
                        icon: UserX,
                      },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        className={`w-full flex items-center px-4 py-2 rounded-md text-left text-sm transition-colors ${
                          activeSubTab === tab.id
                            ? `${
                                darkMode
                                  ? "bg-[#3C3C3C] text-redhalo-mint"
                                  : "rh-accent-soft"
                              }`
                            : `${
                                darkMode
                                  ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                  : "text-gray-600 hover:bg-gray-100"
                              }`
                        }`}
                      >
                        <tab.icon size={16} className="mr-3" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === "attendance" && (
                  <div className="space-y-1">
                    <h3
                      className={`text-sm font-semibold mb-3 ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                      }`}
                    >
                      ATTENDANCE REPORTS
                    </h3>
                    {[
                      {
                        id: "daily-attendance",
                        label: "Daily Attendance Report",
                        icon: Calendar,
                      },
                      {
                        id: "monthly-attendance",
                        label: "Monthly Attendance Report",
                        icon: FileText,
                      },
                      {
                        id: "leave-report",
                        label: "Leave Report",
                        icon: Clock,
                      },
                      {
                        id: "overtime-report",
                        label: "Overtime Report",
                        icon: SafeTrendingUp,
                      },
                      {
                        id: "lop-reversal-report",
                        label: "LOP Reversal Report",
                        icon: FileText,
                      },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        className={`w-full flex items-center px-4 py-2 rounded-md text-left text-sm transition-colors ${
                          activeSubTab === tab.id
                            ? `${
                                darkMode
                                  ? "bg-[#3C3C3C] text-redhalo-mint"
                                  : "rh-accent-soft"
                              }`
                            : `${
                                darkMode
                                  ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                  : "text-gray-600 hover:bg-gray-100"
                              }`
                        }`}
                      >
                        <tab.icon size={16} className="mr-3" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === "payroll" && (
                  <div className="space-y-1">
                    <h3
                      className={`text-sm font-semibold mb-3 ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                      }`}
                    >
                      PAYROLL REPORTS
                    </h3>
                    {[
                      { id: "payslips", label: "Payslips", icon: FileText },
                      { id: "form-16", label: "Form-16", icon: FileText },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        className={`w-full flex items-center px-4 py-2 rounded-md text-left text-sm transition-colors ${
                          activeSubTab === tab.id
                            ? `${
                                darkMode
                                  ? "bg-[#3C3C3C] text-redhalo-mint"
                                  : "rh-accent-soft"
                              }`
                            : `${
                                darkMode
                                  ? "text-[#A6A9C8] hover:bg-[#3C3C3C]"
                                  : "text-gray-600 hover:bg-gray-100"
                              }`
                        }`}
                      >
                        <tab.icon size={16} className="mr-3" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              </div>
            )}

            {/* Main Content Area */}
            <div className={`flex min-h-0 flex-1 flex-col ${hideSidebar ? "" : "p-6"}`}>
              {/* Report Content */}
              <div
                className={
                  hideSidebar
                    ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                    : "ds-page-panel overflow-hidden"
                }
              >
                <div className="border-b border-[hsl(var(--ds-border))] px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="ds-h2 text-[hsl(var(--ds-fg))]">
                        {[
                          { id: "employees", label: "Employees Report" },
                          { id: "age-report", label: "Employee Age Report" },
                          {
                            id: "service-report",
                            label: "Employee Service Report",
                          },
                          { id: "salary-report", label: "Salary Report" },
                          { id: "confirmation", label: "Confirmation Report" },
                          { id: "transfers", label: "Transfers Report" },
                          {
                            id: "salary-revision",
                            label: "Salary Revision Report",
                          },
                          { id: "resignations", label: "Resignations Report" },
                        ].find((tab) => tab.id === activeSubTab)?.label ||
                          "Report"}
                      </h2>
                      <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
                        {activeSubTab === "salary-revision"
                          ? "Generate and download salary revision history for the selected month"
                          : "Generate and download comprehensive employee reports"}
                      </p>
                    </div>
                    {/* Employees, Age, Service reports: Filters and Download in header (no From/To/Load) */}
                    {(activeSubTab === "employees" || activeSubTab === "age-report" || activeSubTab === "service-report") && (
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setShowFilterPopup(true)}
                          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                            "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          } ${hasActiveFiltersForReport ? "ring-2 ring-[hsl(var(--ds-primary))] ring-offset-1" : ""}`}
                          style={hasActiveFiltersForReport ? { animation: "blink-border 1.5s ease-in-out infinite" } : {}}
                        >
                          <Filter size={18} className="mr-2" />
                          Filters
                        </button>
                        {canDownload && (
                          <button
                            type="button"
                            onClick={() =>
                              exportReport(
                                [
                                  { id: "employees", label: "Employees Report" },
                                  { id: "age-report", label: "Employee Age Report" },
                                  { id: "service-report", label: "Employee Service Report" },
                                ].find((t) => t.id === activeSubTab)?.label || "Employee Report"
                              )
                            }
                            className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${
                              "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                            }`}
                          >
                            <Download size={18} className="mr-2" />
                            Download
                          </button>
                        )}
                      </div>
                    )}
                    {/* Salary, Confirmation, Transfers, Resignations, Salary Revision: Select Month only at top */}
                    {["salary-report", "confirmation", "transfers", "resignations", "salary-revision"].includes(activeSubTab) && (
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <label className={`text-sm font-medium ${"text-[hsl(var(--ds-fg))]"}`}>Select Month:</label>
                        <input
                          type="month"
                          min={companyMinMonth}
                          value={
                            activeSubTab === "salary-revision"
                              ? selectedMonth
                              : activeSubTab === "transfers"
                                ? transfersReportMonth
                                : activeSubTab === "resignations"
                                  ? resignationsReportMonth
                                  : activeSubTab === "confirmation"
                                    ? confirmationReportMonth
                                    : employeeReportMonth
                          }
                          onChange={(e) => {
                            const v = clampYm(
                              e.target.value,
                              companyMinMonth,
                              null
                            );
                            if (activeSubTab === "salary-revision") {
                              setSelectedMonth(v);
                              setAdvancedFiltersState({});
                              setAdvancedFiltersParams({});
                            } else if (activeSubTab === "transfers") setTransfersReportMonth(v);
                            else if (activeSubTab === "resignations") setResignationsReportMonth(v);
                            else if (activeSubTab === "confirmation") setConfirmationReportMonth(v);
                            else setEmployeeReportMonth(v);
                          }}
                          disabled={loading}
                          className={`px-3 py-2 border rounded-md text-sm ${"border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"} ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Below heading: Filters + Download only (Select Month is at top for all) */}
                {["salary-report", "confirmation", "transfers", "salary-revision", "resignations"].includes(activeSubTab) && (
                  <div className={`px-6 py-3 flex flex-wrap items-center justify-end gap-3 border-t ${"bg-[hsl(var(--ds-muted)/0.35)] border-[hsl(var(--ds-border))]"}`}>
                    {activeSubTab === "salary-revision" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowFilters(true)}
                          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${"border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"}`}
                        >
                          <Filter size={18} className="mr-2" />
                          Filters
                        </button>
                        {canDownload && (
                          <button
                            type="button"
                            onClick={() => exportReport("Salary Revision Report")}
                            className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${"bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"}`}
                          >
                            <Download size={18} className="mr-2" />
                            Download
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowFilterPopup(true)}
                          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${"border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"} ${hasActiveFiltersForReport ? "ring-2 ring-[hsl(var(--ds-primary))] ring-offset-1" : ""}`}
                          style={hasActiveFiltersForReport ? { animation: "blink-border 1.5s ease-in-out infinite" } : {}}
                        >
                          <Filter size={18} className="mr-2" />
                          Filters
                        </button>
                        {canDownload && (
                          <button
                            type="button"
                            onClick={() =>
                              exportReport(
                                [
                                  { id: "salary-report", label: "Salary Report" },
                                  { id: "confirmation", label: "Confirmation Report" },
                                  { id: "transfers", label: "Transfers Report" },
                                  { id: "resignations", label: "Resignations Report" },
                                ].find((t) => t.id === activeSubTab)?.label || "Report"
                              )
                            }
                            className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${"bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"}`}
                          >
                            <Download size={18} className="mr-2" />
                            Download
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Placeholder for all 8 reports: "Ready to Load" when no data, "Summary loaded" when data loaded (no table) */}
                {(() => {
                  if (!["employees", "age-report", "service-report", "salary-report", "confirmation", "transfers", "salary-revision", "resignations"].includes(activeSubTab)) return null;
                  if (activeSubTab === "salary-revision" && loading) return null;
                  if (activeSubTab === "salary-revision" && selectedMonth && salaryRevisionReportData.length === 0) return null;
                  const isConfirmationLoading = activeSubTab === "confirmation" && confirmationReportMonth && confirmationSummaryLoading;
                  const isTransfersLoading = activeSubTab === "transfers" && transfersReportMonth && transfersSummaryLoading;
                  const isResignationsLoading = activeSubTab === "resignations" && resignationsReportMonth && resignationsSummaryLoading;
                  const isSalaryLoading = activeSubTab === "salary-report" && employeeReportMonth && salarySummaryLoading;
                  const isEmployeesReportLoading = activeSubTab === "employees" && employeesReportSummaryLoading;
                  const isAgeReportLoading = activeSubTab === "age-report" && ageReportSummaryLoading;
                  const isServiceReportLoading = activeSubTab === "service-report" && serviceReportSummaryLoading;

                  const hasData = activeSubTab === "salary-revision"
                    ? salaryRevisionReportData.length > 0
                    : activeSubTab === "transfers"
                      ? transfersReportCount !== null
                      : activeSubTab === "resignations"
                        ? resignationsReportCount !== null
                      : activeSubTab === "salary-report"
                        ? salaryReportCount !== null
                      : activeSubTab === "confirmation"
                        ? confirmationReportCount !== null
                      : activeSubTab === "employees"
                        ? employeesReportCount !== null
                      : activeSubTab === "age-report"
                        ? ageReportCount !== null
                      : activeSubTab === "service-report"
                        ? serviceReportCount !== null
                        : loadedEmployees.length > 0;
                  const loadedEmptyForMonth = activeSubTab === "confirmation" && confirmationReportMonth && showEmployeeList && confirmationReportCount === 0;
                  const transfersEmptyForMonth = activeSubTab === "transfers" && transfersReportMonth && showEmployeeList && transfersReportCount === 0;
                  const resignationsEmptyForMonth = activeSubTab === "resignations" && resignationsReportMonth && showEmployeeList && resignationsReportCount === 0;
                  const salaryReportEmptyForMonth = activeSubTab === "salary-report" && employeeReportMonth && showEmployeeList && salaryReportCount === 0;
                  const employeesReportEmpty = activeSubTab === "employees" && showEmployeeList && employeesReportCount === 0;
                  const ageReportEmpty = activeSubTab === "age-report" && showEmployeeList && ageReportCount === 0;
                  const serviceReportEmpty = activeSubTab === "service-report" && showEmployeeList && serviceReportCount === 0;
                  const dateRangeEmpty = false;
                  const isEmptyForMonth = loadedEmptyForMonth || transfersEmptyForMonth || resignationsEmptyForMonth || salaryReportEmptyForMonth || employeesReportEmpty || ageReportEmpty || serviceReportEmpty || dateRangeEmpty;
                  const formatMonth = (ym) => {
                    if (!ym) return "";
                    const [year, monthNum] = ym.split("-");
                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                    return `${monthNames[parseInt(monthNum, 10) - 1]} ${year}`;
                  };

                  const getEmptyMessage = () => {
                    if (["employees", "age-report", "service-report"].includes(activeSubTab)) {
                      return "No employees found matching the applied filters.";
                    }
                    const ym = activeSubTab === "salary-revision"
                      ? selectedMonth
                      : activeSubTab === "transfers"
                        ? transfersReportMonth
                        : activeSubTab === "resignations"
                          ? resignationsReportMonth
                          : activeSubTab === "confirmation"
                            ? confirmationReportMonth
                            : activeSubTab === "salary-report"
                              ? employeeReportMonth
                              : employeeReportMonth;
                    if (!ym) return "No data found";
                    const [year, monthNum] = ym.split("-");
                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                    const monthName = monthNum ? monthNames[parseInt(monthNum, 10) - 1] : "";
                    const monthYearStr = monthName && year ? `${monthName}-${year}` : formatMonth(ym);
                    switch (activeSubTab) {
                      case "transfers": return `No transfers found for ${monthYearStr}`;
                      case "confirmation": return `No employees confirmed in ${monthYearStr}`;
                      case "resignations": return `No resignations found for ${monthYearStr}`;
                      case "salary-report": return `No salary report data found for ${monthYearStr}`;
                      default: return `No data found for ${monthYearStr}`;
                    }
                  };

                  return (
                    <div className="flex-1 flex items-center justify-center py-12 min-h-[300px]">
                      <div className={`text-center max-w-md ${darkMode ? "text-[#A6A9C8]" : "text-gray-500"}`}>
                        {isEmptyForMonth ? (
                          <>
                            <div className="mb-4 flex justify-center">
                              <ClipboardList size={80} strokeWidth={1.5} className={`${darkMode ? "text-[#4C4C4C]" : "text-gray-300"}`} />
                            </div>
                            <p className={`text-lg font-medium ${"text-[hsl(var(--ds-fg))]"}`}>
                              {getEmptyMessage()}
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="mb-4">
                              <FileText size={64} className={`mx-auto ${darkMode ? "text-[#4C4C4C]" : "text-gray-300"}`} />
                            </div>
                            <h3 className="text-lg font-medium mb-2" style={{ color: hasData ? "green" : "#000000" }}>
                            {(isConfirmationLoading || isTransfersLoading || isResignationsLoading || isSalaryLoading || isEmployeesReportLoading || isAgeReportLoading || isServiceReportLoading) ? "Loading..." : hasData ? "Data loaded successfully" : "Ready to Load Employee Data"}
                            </h3>
                            {(isConfirmationLoading || isTransfersLoading || isResignationsLoading || isSalaryLoading || isEmployeesReportLoading || isAgeReportLoading || isServiceReportLoading) ? (
                              <p className="text-sm">Loading {activeSubTab === "transfers" ? "transfers count" : activeSubTab === "resignations" ? "resignations count" : activeSubTab === "salary-report" ? "salary report count" : activeSubTab === "confirmation" ? "confirmation count" : activeSubTab === "employees" ? "employee count" : activeSubTab === "age-report" ? "age report count" : activeSubTab === "service-report" ? "service report count" : "employees"}...</p>
                            ) : hasData ? (
                              <>
                                <p className={`text-sm mt-3 mb-1 ${"text-[hsl(var(--ds-fg))]"}`}>
                                  {activeSubTab === "employees" ? (
                                    <>Filters applied. <strong>{employeesReportCount ?? 0}</strong> employee{(employeesReportCount ?? 0) !== 1 ? "s" : ""} match your criteria.</>
                                  ) : activeSubTab === "age-report" ? (
                                    <>Filters applied. <strong>{ageReportCount ?? 0}</strong> employee{(ageReportCount ?? 0) !== 1 ? "s" : ""} match your criteria.</>
                                  ) : activeSubTab === "service-report" ? (
                                    <>Filters applied. <strong>{serviceReportCount ?? 0}</strong> employee{(serviceReportCount ?? 0) !== 1 ? "s" : ""} match your criteria.</>
                                  ) : (
                                    <>Employees included: {activeSubTab === "salary-revision"
                                      ? salaryRevisionReportData.length
                                      : activeSubTab === "transfers"
                                        ? transfersReportCount ?? 0
                                        : activeSubTab === "resignations"
                                          ? resignationsReportCount ?? 0
                                          : activeSubTab === "salary-report"
                                            ? salaryReportCount ?? 0
                                          : activeSubTab === "confirmation"
                                            ? confirmationReportCount ?? 0
                                            : loadedEmployees.length}</>
                                  )}
                                </p>
                                <p className="text-sm mb-3">
                                  {activeSubTab === "salary-revision"
                                    ? `Month: ${formatMonth(selectedMonth) || "-"}`
                                    : ["employees", "age-report", "service-report"].includes(activeSubTab)
                                      ? "Click Download to generate the report (same filters as applied above)."
                                      : `Month: ${formatMonth(
                                          activeSubTab === "transfers"
                                            ? transfersReportMonth
                                            : activeSubTab === "resignations"
                                              ? resignationsReportMonth
                                              : activeSubTab === "confirmation"
                                                ? confirmationReportMonth
                                                : employeeReportMonth
                                        ) || "-"}`}
                                </p>
                                <p className="text-sm">Click Download to generate the report.</p>
                              </>
                            ) : ["employees", "age-report", "service-report"].includes(activeSubTab) ? (
                              <>
                                <p className="text-sm mb-1">Click <strong>Filters</strong> to refine criteria, then <strong>Download</strong> to generate the report.</p>
                                <p className="text-xs text-gray-500">Download will fetch data using the current filters.</p>
                              </>
                            ) : (
                              <p className="text-sm">Select a month above and to load the summary, then click on <strong>Download</strong> to generate the report.</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Loading indicator for salary revision report - Show when loading, hide table */}
                {activeSubTab === "salary-revision" && loading && (
                  <div className="flex-1 flex items-center justify-center py-12 min-h-[400px]">
                    <div className="flex flex-col items-center space-y-4">
                      <div className={`animate-spin rounded-full h-12 w-12 border-4 ${
                        darkMode 
                          ? "border-[#3C3C3C] border-t-redhalo-mint" 
                          : "border-gray-200 border-t-redhalo-red"
                      }`}></div>
                      <p className={`text-sm font-medium ${
                        "text-[hsl(var(--ds-fg))]"
                      }`}>
                        Loading data...
                      </p>
                    </div>
                  </div>
                )}

                {/* Empty state message for salary revision report when no data */}
                {activeSubTab === "salary-revision" && showEmployeeList && !loading && salaryRevisionReportData.length === 0 && (
                  <div className="flex-1 flex items-center justify-center py-12 min-h-[400px]">
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4 flex justify-center">
                        <ClipboardList size={80} strokeWidth={1.5} className={`${darkMode ? "text-[#4C4C4C]" : "text-gray-300"}`} />
                      </div>
                      <p className={`text-lg font-medium ${"text-[hsl(var(--ds-fg))]"}`}>
                        {selectedMonth ? (() => {
                          const [year, monthNum] = selectedMonth.split('-');
                          const monthNames = [
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ];
                          const monthName = monthNames[parseInt(monthNum) - 1];
                          return `No salary revisions found for ${monthName}-${year}`;
                        })() : "Please select a month to view salary revisions."}
                      </p>
                    </div>
                  </div>
                )}

                {/* Employee List Container - Hide table for all 8 reports (download-only; summary placeholder shown instead) */}
                {showEmployeeList && !loading && (activeSubTab !== "salary-revision" || (activeSubTab === "salary-revision" && salaryRevisionReportData.length > 0)) && !["salary-report", "confirmation", "transfers", "salary-revision", "resignations", "employees", "age-report", "service-report"].includes(activeSubTab) && (
                  <div className="employee-list-container flex-1 flex flex-col h-[600px]">
                    <div className="flex-shrink-0">
                      <div
                        className={`px-4 pt-4 pb-2 ${
                          darkMode ? "bg-[#1C1C1C]" : "bg-white"
                        } flex items-center justify-between`}
                      >
                        <h3
                          className={`text-lg font-semibold ${
                            darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                          }`}
                        >
                          Employee List ({loadedEmployees.length} employees)
                        </h3>
                        <div className="flex items-center space-x-3">
                          {/* Filters and Download for these reports are only in the bottom bar */}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 overflow-hidden">
                      <div className="h-full overflow-y-auto">
                        <table
                          className={`w-full text-sm employee-table ${
                            darkMode
                              ? "bg-[#2C2C2C] text-[#E0E0E0]"
                              : "bg-white text-gray-900"
                          }`}
                        >
                          <thead
                            className={`${
                              darkMode ? "bg-[#3C3C3C]" : "bg-gray-100"
                            } sticky top-0 z-40`}
                          >
                            <tr>
                              {activeSubTab === "salary-revision" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">SL No</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Emp Code</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Emp Name</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Action Date</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Increment Effective Date</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Previous CTC</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">New CTC</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Increment Amount</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Increment %</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Previous Designation</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">New Designation</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Designation Changed</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Reason</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Remarks</th>
                                </>
                              ) : activeSubTab === "age-report" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    SL No
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employee Code
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employee Name
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Branch
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Department
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Designation
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Category
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employee Type
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Date of Joining
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    DOJ Age
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Employment Status
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Date of Birth
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                Date of Relieving
                                  </th>
                                </>
                              ) : activeSubTab === "service-report" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    SL No
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Emp Code
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Emp Name
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Branch
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Department
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Designation
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Sub Department
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Grade
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Category
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employment Type
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employment Status
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Date of Joining (DOJ)
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Date of Relieving
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Service
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Reporting Manager
                                  </th>
                                </>
                              ) : activeSubTab === "salary-report" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    SL No
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Emp Code
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Emp Name
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Branch
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Department
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Designation
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Grade
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Category
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employment Type
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Date of Joining (DOJ)
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    CTC
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    PF Applicable
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    ESI Applicable
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Bank Name
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Account Number
                                  </th>
                                </>
                              ) : activeSubTab === "confirmation" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    SL No
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Emp Code
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Emp Name
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Branch
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Department
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Designation
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Date of Joining (DOJ)
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Confirmation Date
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Probation Extended
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Extended Probation Date
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Remarks
                                  </th>
                                </>
                              ) : activeSubTab === "transfers" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">SL No</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Emp Code</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Emp Name</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Effective Date</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">From Branch</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">To Branch</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">From Department</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">To Department</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">From Designation</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">To Designation</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">From Grade</th>
                                  <th className="px-2 py-2 text-center fo nt-semibold text-xs capitalize tracking-wider">To Grade</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">From Category</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">To Category</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Reason</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Remarks</th>
                                </>
                              ) : activeSubTab === "resignations" ? (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">SL No</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Emp Code</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Emp Name</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Branch</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Department</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Designation</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Date of Joining (DOJ)</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Resignation Date</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Relieving Date</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Notice Period (Days)</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Notice Duration</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Notice Waived (Days)</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Notice to Deduct</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Reason</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Retro Resignation</th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">Remarks</th>
                                </>
                              ) : (
                                <>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    SL No
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Employee Code
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Name
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Department
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Designation
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    {getReportDateLabel(activeSubTab)}
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold text-xs capitalize tracking-wider">
                                    Status
                                  </th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {loadedEmployees.length > 0 ? (
                              loadedEmployees.map((item, index) => {
                                if (activeSubTab === "salary-revision") {
                                  const revision = item;
                                  const employee = revision.employee;
                                  const prevCtc = revision.previousCtc ?? 0;
                                  const newCtc = revision.ctcAmount ?? revision.newCtc ?? 0;
                                  const incrementAmt = (prevCtc != null && newCtc != null) ? (newCtc - prevCtc) : null;
                                  const incrementPct = (prevCtc != null && prevCtc > 0 && incrementAmt != null)
                                    ? calculatePercentageChange(prevCtc, newCtc)
                                    : null;
                                  const prevDesig = designations.find((d) => d.id === employee?.designationId)?.name ?? "";
                                  const newDesig = revision.designation ?? "";
                                  const designationChanged = newDesig && prevDesig !== newDesig;
                                  return (
                                    <tr
                                      key={`${employee?.id}-${revision.id}-${index}`}
                                      className={`border-b ${darkMode ? "border-[#3C3C3C] hover:bg-[#3C3C3C]" : "border-gray-200 hover:bg-gray-50"}`}
                                    >
                                      <td className="px-2 py-2 text-center">{index + 1}</td>
                                      <td className="px-2 py-2 text-center font-medium">{employee?.empCode || "-"}</td>
                                      <td className="px-2 py-2 text-center">{employee?.user?.name || employee?.name || "-"}</td>
                                      <td className="px-2 py-2 text-center">{revision.actionDate || revision.createdAt ? new Date(revision.actionDate || revision.createdAt).toLocaleDateString() : "-"}</td>
                                      <td className="px-2 py-2 text-center">{revision.effectiveDate ? new Date(revision.effectiveDate).toLocaleDateString() : "-"}</td>
                                      <td className="px-2 py-2 text-center">{prevCtc != null && prevCtc !== "" ? (typeof prevCtc === "number" ? `₹${formatIndianNumber(prevCtc)}` : prevCtc) : "-"}</td>
                                      <td className="px-2 py-2 text-center">{newCtc != null && newCtc !== "" ? (typeof newCtc === "number" ? `₹${formatIndianNumber(newCtc)}` : newCtc) : "-"}</td>
                                      <td className="px-2 py-2 text-center">{incrementAmt != null ? `₹${formatIndianNumber(incrementAmt)}` : "-"}</td>
                                      <td className="px-2 py-2 text-center">{incrementPct != null ? `${incrementPct.toFixed(2)}%` : "-"}</td>
                                      <td className="px-2 py-2 text-center">{prevDesig || "-"}</td>
                                      <td className="px-2 py-2 text-center">{newDesig || "-"}</td>
                                      <td className="px-2 py-2 text-center">{designationChanged ? "Yes" : "No"}</td>
                                      <td className="px-2 py-2 text-center">{revision.reason || "-"}</td>
                                      <td className="px-2 py-2 text-center">{revision.remarks || "-"}</td>
                                    </tr>
                                  );
                                } else {
                                  const employee = item;
                                  if (activeSubTab === "age-report") {
                                    const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
                                    const dob = employee.user?.dateOfBirth || employee.dateOfBirth;
                                  const relievingDate =
                                    employee.relievingDate ??
                                    employee.dateOfRelieving ??
                                    employee.dateOfLeaving ??
                                    employee.relievedDate;
                                    return (
                                      <tr
                                        key={employee.id}
                                        className={`border-b ${
                                          darkMode
                                            ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                                            : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                      >
                                        <td className="px-2 py-2 text-center">
                                          {index + 1}
                                        </td>
                                        <td className="px-2 py-2 text-center font-medium">
                                          {employee.empCode || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.user?.name || employee.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {branches.find((b) => b.id === employee.branchId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {departments.find(
                                            (d) => d.id === employee.departmentId
                                          )?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {designations.find(
                                            (d) => d.id === employee.designationId
                                          )?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {categories.find(
                                            (c) => c.id === employee.categoryId
                                          )?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.employmentType || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {doj
                                            ? new Date(doj).toLocaleDateString()
                                            : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {getAgeAtDate(dob, doj)}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          <span
                                            className={`px-2 py-1 rounded-md text-xs ${
                                              employee.employmentStatus === "active"
                                                ? "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                                : "border border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300"
                                            }`}
                                          >
                                            {employee.employmentStatus || "Active"}
                                          </span>
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {dob
                                            ? new Date(dob).toLocaleDateString()
                                            : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {relievingDate
                                            ? new Date(relievingDate).toLocaleDateString()
                                            : "-"}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  if (activeSubTab === "service-report") {
                                    const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
                                    const relievingDate =
                                      employee.relievingDate ??
                                      employee.dateOfRelieving ??
                                      employee.dateOfLeaving ??
                                      employee.relievedDate;
                                    const service = getServiceYearsMonths(doj, relievingDate || new Date());
                                    const reportingManager = loadedEmployees.find(
                                      (e) => e.id === employee.reportingManagerId
                                    );
                                    return (
                                      <tr
                                        key={employee.id}
                                        className={`border-b ${
                                          darkMode
                                            ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                                            : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                      >
                                        <td className="px-2 py-2 text-center">{index + 1}</td>
                                        <td className="px-2 py-2 text-center font-medium">
                                          {employee.empCode || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.user?.name || employee.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {branches.find((b) => b.id === employee.branchId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {departments.find((d) => d.id === employee.departmentId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {designations.find((d) => d.id === employee.designationId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {subDepartments.find((d) => d.id === employee.subDepartmentId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {grades.find((g) => g.id === employee.gradeId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {categories.find((c) => c.id === employee.categoryId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.employmentType || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          <span
                                            className={`px-2 py-1 rounded-md text-xs ${
                                              employee.employmentStatus === "active"
                                                ? "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                                : "border border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300"
                                            }`}
                                          >
                                            {employee.employmentStatus || "Active"}
                                          </span>
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {doj ? new Date(doj).toLocaleDateString() : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {relievingDate
                                            ? new Date(relievingDate).toLocaleDateString()
                                            : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {service.years === "-" || service.months === "-"
                                            ? "-"
                                            : `${service.years}.${service.months}`}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.reportingManager?.user?.name ||
                                            employee.reportingManager?.name ||
                                            reportingManager?.user?.name ||
                                            reportingManager?.name ||
                                            "-"}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  if (activeSubTab === "salary-report") {
                                    const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
                                    return (
                                      <tr
                                        key={employee.id}
                                        className={`border-b ${
                                          darkMode
                                            ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                                            : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                      >
                                        <td className="px-2 py-2 text-center">{index + 1}</td>
                                        <td className="px-2 py-2 text-center font-medium">
                                          {employee.empCode || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.user?.name || employee.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {branches.find((b) => b.id === employee.branchId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {departments.find((d) => d.id === employee.departmentId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {designations.find((d) => d.id === employee.designationId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {grades.find((g) => g.id === employee.gradeId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {categories.find((c) => c.id === employee.categoryId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.employmentType || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {doj ? new Date(doj).toLocaleDateString() : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.ctc != null && employee.ctc !== ""
                                            ? (typeof employee.ctc === "number"
                                                ? `₹${formatIndianNumber(employee.ctc)}`
                                                : employee.ctc)
                                            : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.pfApplicable ? "Yes" : "No"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.esiApplicable ? "Yes" : "No"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.bankName || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.accountNumber || "-"}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  if (activeSubTab === "confirmation") {
                                    const doj = employee.dateOfJoin || employee.joiningDate || employee.joinDate;
                                    const probationExtended = employee.extendProbation ?? employee.probationExtended ?? false;
                                    const extendedDate = employee.extendedProbationDate;
                                    const remarks = employee.remarks ?? employee.confirmationRemarks ?? "";
                                    return (
                                      <tr
                                        key={employee.id}
                                        className={`border-b ${
                                          darkMode
                                            ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                                            : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                      >
                                        <td className="px-2 py-2 text-center">{index + 1}</td>
                                        <td className="px-2 py-2 text-center font-medium">
                                          {employee.empCode || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.user?.name || employee.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {branches.find((b) => b.id === employee.branchId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {departments.find((d) => d.id === employee.departmentId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {designations.find((d) => d.id === employee.designationId)?.name || "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {doj ? new Date(doj).toLocaleDateString() : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {employee.confirmationDate
                                            ? new Date(employee.confirmationDate).toLocaleDateString()
                                            : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {probationExtended ? "Yes" : "No"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {extendedDate
                                            ? new Date(extendedDate).toLocaleDateString()
                                            : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                          {remarks || "-"}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  if (activeSubTab === "transfers") {
                                    const emp = item.employee || item;
                                    const effDate = item.effectiveDate ?? item.transferDate ?? item.transferEffectiveDate ?? emp.transferDate ?? emp.transferEffectiveDate;
                                    const fromBranch = branches.find((b) => b.id === (item.fromBranchId ?? emp.fromBranchId))?.name ?? item.fromBranch?.name ?? (typeof item.fromBranch === "string" ? item.fromBranch : null) ?? "-";
                                    const toBranch = branches.find((b) => b.id === (item.toBranchId ?? emp.toBranchId))?.name ?? item.toBranch?.name ?? (typeof item.toBranch === "string" ? item.toBranch : null) ?? "-";
                                    const fromDept = departments.find((d) => d.id === (item.fromDepartmentId ?? emp.fromDepartmentId))?.name ?? item.fromDepartment?.name ?? (typeof item.fromDepartment === "string" ? item.fromDepartment : null) ?? "-";
                                    const toDept = departments.find((d) => d.id === (item.toDepartmentId ?? emp.toDepartmentId))?.name ?? item.toDepartment?.name ?? (typeof item.toDepartment === "string" ? item.toDepartment : null) ?? "-";
                                    const fromDesig = designations.find((d) => d.id === (item.fromDesignationId ?? emp.fromDesignationId))?.name ?? item.fromDesignation?.name ?? (typeof item.fromDesignation === "string" ? item.fromDesignation : null) ?? "-";
                                    const toDesig = designations.find((d) => d.id === (item.toDesignationId ?? emp.toDesignationId))?.name ?? item.toDesignation?.name ?? (typeof item.toDesignation === "string" ? item.toDesignation : null) ?? "-";
                                    const fromGrade = grades.find((g) => g.id === (item.fromGradeId ?? emp.fromGradeId))?.name ?? item.fromGrade?.name ?? (typeof item.fromGrade === "string" ? item.fromGrade : null) ?? "-";
                                    const toGrade = grades.find((g) => g.id === (item.toGradeId ?? emp.toGradeId))?.name ?? item.toGrade?.name ?? (typeof item.toGrade === "string" ? item.toGrade : null) ?? "-";
                                    const fromCat = categories.find((c) => c.id === (item.fromCategoryId ?? emp.fromCategoryId))?.name ?? item.fromCategory?.name ?? (typeof item.fromCategory === "string" ? item.fromCategory : null) ?? "-";
                                    const toCat = categories.find((c) => c.id === (item.toCategoryId ?? emp.toCategoryId))?.name ?? item.toCategory?.name ?? (typeof item.toCategory === "string" ? item.toCategory : null) ?? "-";
                                    const reason = item.reason ?? item.transferReason ?? emp.transferReason ?? emp.reason ?? "-";
                                    const remarksVal = item.remarks ?? item.transferRemarks ?? emp.transferRemarks ?? emp.remarks ?? "-";
                                    return (
                                      <tr key={emp.id || index} className={`border-b ${darkMode ? "border-[#3C3C3C] hover:bg-[#3C3C3C]" : "border-gray-200 hover:bg-gray-50"}`}>
                                        <td className="px-2 py-2 text-center">{index + 1}</td>
                                        <td className="px-2 py-2 text-center font-medium">{emp.empCode ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{emp.user?.name ?? emp.name ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{effDate ? new Date(effDate).toLocaleDateString() : "-"}</td>
                                        <td className="px-2 py-2 text-center">{fromBranch}</td>
                                        <td className="px-2 py-2 text-center">{toBranch}</td>
                                        <td className="px-2 py-2 text-center">{fromDept}</td>
                                        <td className="px-2 py-2 text-center">{toDept}</td>
                                        <td className="px-2 py-2 text-center">{fromDesig}</td>
                                        <td className="px-2 py-2 text-center">{toDesig}</td>
                                        <td className="px-2 py-2 text-center">{fromGrade}</td>
                                        <td className="px-2 py-2 text-center">{toGrade}</td>
                                        <td className="px-2 py-2 text-center">{fromCat}</td>
                                        <td className="px-2 py-2 text-center">{toCat}</td>
                                        <td className="px-2 py-2 text-center">{reason}</td>
                                        <td className="px-2 py-2 text-center">{remarksVal}</td>
                                      </tr>
                                    );
                                  }
                                  if (activeSubTab === "resignations") {
                                    const emp = item;
                                    const doj = emp.dateOfJoin ?? emp.joiningDate ?? emp.joinDate;
                                    const resigDate = emp.resignationDate;
                                    const relievingDate = emp.relievingDate ?? emp.dateOfRelieving ?? emp.dateOfLeaving ?? emp.relievedDate;
                                    const noticePeriod = emp.noticePeriodDays ?? emp.noticePeriod ?? "-";
                                    const noticeWaived = emp.noticePeriodWaiverDays ?? emp.noticeWaived ?? "-";
                                    const noticeToDeduct = emp.noticePeriodToDeduct ?? emp.noticeToDeduct ?? "-";
                                    const reason = emp.resignationReason ?? emp.reason ?? "-";
                                    const retro = emp.retroResignation ?? emp.isRetroResignation ?? false;
                                    const remarksVal = emp.remarks ?? emp.resignationRemarks ?? "-";
                                    return (
                                      <tr key={emp.id} className={`border-b ${darkMode ? "border-[#3C3C3C] hover:bg-[#3C3C3C]" : "border-gray-200 hover:bg-gray-50"}`}>
                                        <td className="px-2 py-2 text-center">{index + 1}</td>
                                        <td className="px-2 py-2 text-center font-medium">{emp.empCode ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{emp.user?.name ?? emp.name ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{branches.find((b) => b.id === emp.branchId)?.name ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{departments.find((d) => d.id === emp.departmentId)?.name ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{designations.find((d) => d.id === emp.designationId)?.name ?? "-"}</td>
                                        <td className="px-2 py-2 text-center">{doj ? new Date(doj).toLocaleDateString() : "-"}</td>
                                        <td className="px-2 py-2 text-center">{resigDate ? new Date(resigDate).toLocaleDateString() : "-"}</td>
                                        <td className="px-2 py-2 text-center">{relievingDate ? new Date(relievingDate).toLocaleDateString() : "-"}</td>
                                        <td className="px-2 py-2 text-center">{noticePeriod}</td>
                                        <td className="px-2 py-2 text-center">
                                          {noticePeriod !== "-" ? String(noticePeriod) : "-"}
                                        </td>
                                        <td className="px-2 py-2 text-center">{noticeWaived}</td>
                                        <td className="px-2 py-2 text-center">{noticeToDeduct}</td>
                                        <td className="px-2 py-2 text-center">{reason}</td>
                                        <td className="px-2 py-2 text-center">{retro ? "Yes" : "No"}</td>
                                        <td className="px-2 py-2 text-center">{remarksVal ?? "-"}</td>
                                      </tr>
                                    );
                                  }
                                  return (
                                    <tr
                                      key={employee.id}
                                      className={`border-b ${
                                        darkMode
                                          ? "border-[#3C3C3C] hover:bg-[#3C3C3C]"
                                          : "border-gray-200 hover:bg-gray-50"
                                      }`}
                                    >
                                      <td className="px-2 py-2 text-center">
                                        {index + 1}
                                      </td>
                                      <td className="px-2 py-2 text-center font-medium">
                                        {employee.empCode}
                                      </td>
                                      <td className="px-2 py-2 text-center">
                                        {employee.user?.name || employee.name}
                                      </td>
                                      <td className="px-2 py-2 text-center">
                                        {departments.find(
                                          (d) => d.id === employee.departmentId
                                        )?.name || "-"}
                                      </td>
                                      <td className="px-2 py-2 text-center">
                                        {designations.find(
                                          (d) => d.id === employee.designationId
                                        )?.name || "-"}
                                      </td>
                                      <td className="px-2 py-2 text-center">
                                        {getEmployeeDateForReport(
                                          employee,
                                          activeSubTab
                                        )
                                          ? new Date(
                                              getEmployeeDateForReport(
                                                employee,
                                                activeSubTab
                                              )
                                            ).toLocaleDateString()
                                          : "-"}
                                      </td>
                                      <td className="px-2 py-2 text-center">
                                        <span
                                          className={`px-2 py-1 rounded-md text-xs ${
                                            employee.employmentStatus === "active"
                                              ? "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                              : "border border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300"
                                          }`}
                                        >
                                          {employee.employmentStatus || "Active"}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                }
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={
                                    activeSubTab === "salary-revision"
                                      ? "14"
                                      : activeSubTab === "age-report"
                                        ? "10"
                                        : activeSubTab === "service-report"
                                          ? "15"
                                          : activeSubTab === "salary-report"
                                            ? "15"
                                            : activeSubTab === "confirmation"
                                              ? "11"
                                              : activeSubTab === "transfers"
                                                ? "16"
                                                : activeSubTab === "resignations"
                                                  ? "15"
                                                  : "7"
                                  }
                                  className="px-4 py-8 text-center text-gray-500"
                                >
                                  {activeSubTab !== "salary-revision" && "No employees found matching the current filters."}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pagination - only for salary-revision; employee reports show all with vertical scroll */}
                {showEmployeeList && loadedEmployees.length > 0 && activeSubTab === "salary-revision" && (
                  <Pagination
                    darkMode={darkMode}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                )}


              </div>

              {/* Attendance Reports */}
              {activeTab === "attendance" && (
                <div
                  className={`rounded-md shadow-lg ${
                    darkMode
                      ? "bg-[#2C2C2C] border border-[#3C3C3C]"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2
                          className={`text-2xl font-bold ${
                            darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                          }`}
                        >
                          {[
                            {
                              id: "daily-attendance",
                              label: "Daily Attendance Report",
                            },
                            {
                              id: "monthly-attendance",
                              label: "Monthly Attendance Report",
                            },
                            { id: "leave-report", label: "Leave Report" },
                            { id: "overtime-report", label: "Overtime Report" },
                            { id: "lop-reversal-report", label: "LOP Reversal Report" },
                          ].find((tab) => tab.id === activeSubTab)?.label ||
                            "Attendance Report"}
                        </h2>
                        <p
                          className={`text-sm mt-1 ${
                            darkMode ? "text-[#A6A9C8]" : "text-gray-600"
                          }`}
                        >
                          Generate and download attendance reports
                        </p>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => setShowFilters(true)}
                          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                            darkMode
                              ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          <Filter size={18} className="mr-2" />
                          Filters
                        </button>
                        <button
                          onClick={() =>
                            exportReport(
                              [
                                {
                                  id: "daily-attendance",
                                  label: "Daily Attendance Report",
                                },
                                {
                                  id: "monthly-attendance",
                                  label: "Monthly Attendance Report",
                                },
                                { id: "leave-report", label: "Leave Report" },
                                {
                                  id: "overtime-report",
                                  label: "Overtime Report",
                                },
                                {
                                  id: "lop-reversal-report",
                                  label: "LOP Reversal Report",
                                },
                              ].find((tab) => tab.id === activeSubTab)?.label
                            )
                          }
                          className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${
                            darkMode
                              ? "bg-redhalo-navy text-white hover:bg-redhalo-navy-light"
                              : "rh-btn-primary hover:opacity-95"
                          }`}
                        >
                          <Download size={18} className="mr-2" />
                          Export
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    {activeSubTab === "lop-reversal-report" ? (
                      <>
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <label className={`text-sm font-medium ${"text-[hsl(var(--ds-fg))]"}`}>Payroll Month</label>
                            <DatePicker
                              monthYearOnly
                              showMonthName
                              monthYearEmptyLabel="Select month"
                              value={
                                lopReversalReportYear != null && lopReversalReportMonth != null
                                  ? `${lopReversalReportYear}-${String(lopReversalReportMonth).padStart(2, "0")}-01`
                                  : ""
                              }
                              onChange={(e) => {
                                const raw = e?.target?.value;
                                if (raw == null || String(raw).trim() === "") {
                                  setLOPReversalReportYear(null);
                                  setLOPReversalReportMonth(null);
                                  return;
                                }
                                const parsed = parseYearMonthFromPickerValue(raw);
                                if (parsed) {
                                  setLOPReversalReportYear(parsed.year);
                                  setLOPReversalReportMonth(parsed.month);
                                }
                              }}
                              min={lopReversalMinDate || undefined}
                              max={new Date()}
                              darkMode={darkMode}
                              dropdownAlign="auto"
                              className="z-50 min-w-[200px]"
                            />
                          </div>
                          {lopReversalReportLoading && (
                            <span className={`text-sm ${darkMode ? "text-[#A6A9C8]" : "text-gray-500"}`}>Loading…</span>
                          )}
                        </div>
                        {(lopReversalReportMonth == null || lopReversalReportYear == null) && !lopReversalReportLoading && (
                          <div
                            className={`text-center py-12 mb-4 rounded border ${
                              darkMode ? "text-[#A6A9C8] border-[#3C3C3C]" : "text-gray-500 border-gray-200"
                            }`}
                          >
                            <div className="mb-4">
                              <FileText
                                size={48}
                                className={`mx-auto ${darkMode ? "text-[#4C4C4C]" : "text-gray-300"}`}
                              />
                            </div>
                            <h3 className="text-lg font-medium mb-2">Ready to Load Employee Data</h3>
                            <p className="text-sm mb-4 max-w-lg mx-auto px-4">
                              Open the filters, select your preferred options, and click Apply to view the employee list.
                            </p>
                            <p className="text-xs max-w-lg mx-auto px-4">
                              After loading the data, you can export the report. Select a payroll month above to load the
                              LOP reversal report.
                            </p>
                          </div>
                        )}
                        {lopReversalReportMonth != null &&
                          lopReversalReportYear != null &&
                          !lopReversalReportLoading &&
                          lopReversalReportFetched &&
                          lopReversalReportData.length === 0 && (
                            <div
                              className={`mb-4 p-6 text-center text-sm rounded border ${
                                darkMode ? "text-[#A6A9C8] border-[#3C3C3C]" : "text-gray-600 border-gray-200"
                              }`}
                            >
                              No LOP reversal records for{" "}
                              <span className="font-semibold">
                                {new Date(lopReversalReportYear, lopReversalReportMonth - 1, 1).toLocaleString("default", {
                                  month: "long",
                                  year: "numeric",
                                })}
                              </span>
                              .
                            </div>
                          )}
                        {lopReversalReportMonth != null &&
                          lopReversalReportYear != null &&
                          lopReversalReportData.length > 0 && (
                          <div className="overflow-x-auto border rounded">
                            <table className={`w-full text-sm ${darkMode ? "text-[#E0E0E0]" : "text-gray-800"}`}>
                              <thead className={darkMode ? "bg-[#3C3C3C]" : "bg-gray-100"}>
                                <tr>
                                  <th className="px-2 py-2 text-left whitespace-nowrap">Sl No</th>
                                  <th className="px-2 py-2 text-left whitespace-nowrap">Employee Code</th>
                                  <th className="px-2 py-2 text-left whitespace-nowrap">Employee Name</th>
                                  <th className="px-2 py-2 text-left whitespace-nowrap">DOJ</th>
                                  <th className="px-2 py-2 text-left whitespace-nowrap w-[96px]">LOP Month</th>
                                  <th className="px-2 py-2 text-right whitespace-nowrap w-[128px]">LOP Reversal Days</th>
                                  <th className="px-2 py-2 text-left min-w-[220px]">Dates</th>
                                  <th className="px-2 py-2 text-left whitespace-nowrap">Payroll Month</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lopReversalReportData.map((row, idx) => (
                                  <tr key={`${row.employeeId}-${row.lopMonth}-${idx}`} className={darkMode ? "border-b border-[#3C3C3C]" : "border-b border-gray-200"}>
                                    <td className="px-2 py-2">{idx + 1}</td>
                                    <td className="px-2 py-2">{row.employeeCode}</td>
                                    <td className="px-2 py-2">{row.employeeName}</td>
                                    <td className="px-2 py-2">{row.doj ? String(row.doj).slice(0, 10) : ""}</td>
                                    <td className="px-2 py-2 whitespace-nowrap w-[96px]">{row.lopMonth}</td>
                                    <td className="px-2 py-2 text-right font-medium whitespace-nowrap w-[128px]">{row.lopReversalDays}</td>
                                    <td className="px-2 py-2 min-w-[220px]">{row.dates}</td>
                                    <td className="px-2 py-2">{row.payrollMonth}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className={`text-center py-12 ${
                          darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                        }`}
                      >
                        <div className="mb-4">
                          <Clock
                            size={48}
                            className={`mx-auto ${
                              darkMode ? "text-[#4C4C4C]" : "text-gray-300"
                            }`}
                          />
                        </div>
                        <h3 className="text-lg font-medium mb-2">
                          Ready to Generate Attendance Report
                        </h3>
                        <p className="text-sm">
                          Click "Export Report" to generate and download the
                          attendance report.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payroll Reports */}
              {activeTab === "payroll" && (
                <div>
                  {activeSubTab === "payslips" && (
                    <Payslips employees={loadedEmployees} />
                  )}
                  {activeSubTab === "form-16" && (
                    <Form16 employees={loadedEmployees} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters Component */}
        {showFilters && (
          <Filters
            onApply={(employeesList, filters, params) => {
              setAdvancedFiltersParams(params || {});
              setAdvancedFiltersState(filters || {});
              setShowFilters(false);
              // Apply filters and fetch data
              if (activeSubTab === "salary-revision") {
                // For salary revision, fetch with filters applied
                fetchSalaryRevisionReport(selectedMonth);
              } else {
                fetchReportData(1);
              }
            }}
            onClose={() => setShowFilters(false)}
            darkMode={darkMode}
            initialFilters={advancedFiltersState}
          />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.darkMode === nextProps.darkMode;
  }
);

export default Reports;
